import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  SelectChangeEvent,
  Autocomplete,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Collapse,
  Tooltip,
  Divider,
  Popover,
  Tabs,
  Tab,
  Pagination,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CalendarToday,
  FilterList,
  Refresh,
  Clear,
  ExpandMore,
  ExpandLess,
  KeyboardArrowDown,
  FileDownload,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import NOCDashboardVisual from './NOCDashboardVisual';

// Helper functions moved outside component to enable state initialization
const formatLocalDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getMondayOfCurrentWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // If Sunday (0), go back 6 days; otherwise go to Monday
  d.setDate(d.getDate() + diff);
  return d;
};

const calculatePresetDates = (preset: 'last8weeks' | 'last4weeks' | 'last2weeks' | 'last7days'): { start: string; end: string } => {
  const today = new Date();
  const endDate = new Date(today);
  let startDate: Date;
  
  switch (preset) {
    case 'last8weeks':
      // Start from Monday of 7 weeks ago (to include current week = 8 weeks total)
      const mondayOfCurrentWeek8 = getMondayOfCurrentWeek(today);
      startDate = new Date(mondayOfCurrentWeek8);
      startDate.setDate(startDate.getDate() - (7 * 7)); // Go back 7 weeks
      break;
    case 'last4weeks':
      // Start from Monday of 3 weeks ago (to include current week = 4 weeks total)
      const mondayOfCurrentWeek4 = getMondayOfCurrentWeek(today);
      startDate = new Date(mondayOfCurrentWeek4);
      startDate.setDate(startDate.getDate() - (3 * 7)); // Go back 3 weeks
      break;
    case 'last2weeks':
      // Start from Monday of 1 week ago (to include current week = 2 weeks total)
      const mondayOfCurrentWeek2 = getMondayOfCurrentWeek(today);
      startDate = new Date(mondayOfCurrentWeek2);
      startDate.setDate(startDate.getDate() - 7); // Go back 1 week
      break;
    case 'last7days':
      // For days, just go back 7 days (not week-aligned)
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 7);
      break;
  }
  
  return {
    start: formatLocalDate(startDate),
    end: formatLocalDate(endDate)
  };
};

// Calculate initial dates for last 8 weeks
const initialDates = calculatePresetDates('last8weeks');

// Types
interface NOCDashboardFilters {
  start_date_opened?: string;
  end_date_opened?: string;
  start_date_resolved?: string;
  end_date_resolved?: string;
  grouping: 'weekly' | 'monthly';
  group_by: 'resolved_at' | 'opened_at';
  ci_class?: string;
  close_code?: string;
  opened_by?: string;
  vendor_ticket?: 'yes' | 'no';
  assignment_group?: string;
  location?: string;
  state?: string;
  resolved_by_noc?: 'yes' | 'no';
  detected_by_alert?: boolean;
  has_parent_incident?: boolean;
}

type YesNoAll = '' | 'yes' | 'no';

interface ResolutionBucket {
  period: string;
  managedByNOC: number;
  mttrManagedByNOC: number;
  resolvedByNOC: number;
  mttrResolvedByNOC: number;
  resolvedByOther: number;
  mttrResolvedByOther: number;
  cancelled: number;
  mttrCancelled: number;
  p90TTR: number;
}

interface ResolutionCodeSplit {
  period: string;
  codes: { [key: string]: number };
}

interface LocationSplit {
  period: string;
  locations: { [key: string]: number };
}

interface CINameSplit {
  period: string;
  ciNames: { [key: string]: number };
}

interface CIClassSplit {
  period: string;
  ciClasses: { [key: string]: number };
}

interface RawIncidentRecord {
  number: string;
  opened_at: string;
  resolved_at: string | null;
  assignment_group_dv: string;
  close_code: string | null;
  state_dv: string;
  hold_reason: string;
  u_sla_assignment_groups_dv: string;
  cmdb_ci_dv: string;
  priority: string;
  u_vendor_name_dv: string;
  category_dv: string;
  subcategory_dv: string;
  opened_by: string;
  ci_class: string;
  ci_location: string;
  mttr_minutes: number | null;
}

interface RawDataPagination {
  total_records: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  has_next: boolean;
  has_prev: boolean;
}

interface RawDataResponse {
  status: string;
  data: RawIncidentRecord[];
  pagination: RawDataPagination;
}

interface MTTRBucket {
  period: string;
  buckets: {
    '0-10 mins': number;
    '10-30 mins': number;
    '30 mins - 24 hours': number;
    '24 - 48 hours': number;
    '48 - 72 hours': number;
    '>72 hours': number;
  };
}

interface StateAndHoldReason {
  period: string;
  stateReasons: {
    [state: string]: {
      count: number;
      holdReasons: { [reason: string]: number };
    };
  };
}

interface NOCTransfer {
  period: string;
  startedAtNOC: number;
  sentOut: number;
  percentageSentOut: number;
  solvedByAutomation: number;
}

interface TransferDestination {
  period: string;
  destinations: {
    [destination: string]: {
      count: number;
      percentage: number;
    };
  };
  total: number;
}

interface OBSIncident {
  period: string;
  count: number;
}

interface SlicerOptions {
  ciClasses: string[];
  closeCodes: string[];
  openedBy: string[];
  assignmentGroups: string[];
  locations: string[];
  states: string[];
  dateRange: {
    opened_at: {
      min: string;
      max: string;
    };
    resolved_at: {
      min: string;
      max: string;
    };
  };
}

interface APIResponse {
  status: string;
  data: {
    metadata: {
      totalRecords: number;
      dateRangeOpened: { start: string | null; end: string | null };
      dateRangeResolved: { start: string | null; end: string | null };
      grouping: 'weekly' | 'monthly';
      groupBy: 'resolved_at' | 'opened_at';
    };
    resolutionBuckets: ResolutionBucket[];
    resolutionCodeSplit: ResolutionCodeSplit[];
    locationSplit: LocationSplit[];
    ciNameSplit: CINameSplit[];
    ciClassSplit: CIClassSplit[];
    mttrBuckets: MTTRBucket[];
    stateAndHoldReasons: StateAndHoldReason[];
    nocTransfers: {
      timeSeries: NOCTransfer[];
      total: number;
      totalSentOut: number;
      overallPercentage: number;
      totalSolvedByAutomation: number;
    };
    transferDestinations: {
      timeSeries: TransferDestination[];
      overallDestinations: {
        [destination: string]: {
          count: number;
          percentage: number;
        };
      };
      totalTransfers: number;
    };
    obsIncidents: {
      timeSeries: OBSIncident[];
      total: number;
    };
    slicerOptions: SlicerOptions;
  };
}

interface NOCDashboardProps {
  accessToken: string | null;
  graphData: any;
}

// Extracted outside component to prevent re-creation on every render
const SortableHeaderComponent: React.FC<{
  column: string;
  label: string;
  currentSortColumn: string | null;
  currentSortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  align?: 'left' | 'center' | 'right';
  sx?: any;
}> = ({ column, label, currentSortColumn, currentSortDirection, onSort, align = 'center', sx = {} }) => {
  const isActive = currentSortColumn === column;
  return (
    <TableCell 
      align={align} 
      sx={{ 
        ...sx,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { backgroundColor: '#f0f0f0' }
      }}
      onClick={() => onSort(column)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', gap: 0.5 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        {isActive ? (
          currentSortDirection === 'asc' ? (
            <ArrowUpward sx={{ fontSize: 16 }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 16 }} />
          )
        ) : (
          <ArrowDownward sx={{ fontSize: 16, opacity: 0.3 }} />
        )}
      </Box>
    </TableCell>
  );
};

const SortableHeader = React.memo(SortableHeaderComponent);

const NOCDashboard: React.FC<NOCDashboardProps> = ({ accessToken, graphData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<APIResponse['data'] | null>(null);
  const [slicerOptions, setSlicerOptions] = useState<SlicerOptions | null>(null);

  // Filter states - initialize with last 8 weeks dates to prevent initial fetch without dates
  const [filters, setFilters] = useState<NOCDashboardFilters>({
    grouping: 'weekly',
    group_by: 'opened_at',
    start_date_opened: initialDates.start,
    end_date_opened: initialDates.end,
  });

  // Period selection (Year/Month/Week) - Separate for Opened and Resolved
  const [openedYear, setOpenedYear] = useState<string>('');
  const [openedMonth, setOpenedMonth] = useState<string>('');
  const [openedWeek, setOpenedWeek] = useState<string>('');
  const [resolvedYear, setResolvedYear] = useState<string>('');
  const [resolvedMonth, setResolvedMonth] = useState<string>('');
  const [resolvedWeek, setResolvedWeek] = useState<string>('');

  // Multi-select states
  const [selectedCIClasses, setSelectedCIClasses] = useState<string[]>([]);
  const [selectedCloseCodes, setSelectedCloseCodes] = useState<string[]>([]);
  const [selectedOpenedBy, setSelectedOpenedBy] = useState<string[]>([]);
  const [selectedAssignmentGroups, setSelectedAssignmentGroups] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  
  // Constant for Select All option
  const SELECT_ALL_OPTION = 'Select All';
  const [vendorTicket, setVendorTicket] = useState<'' | 'yes' | 'no'>('');
  const [resolvedBy, setResolvedBy] = useState<'' | 'NOC' | 'Others'>('');
  const [detectedByAlert, setDetectedByAlert] = useState<YesNoAll>('');
  const [parentIncident, setParentIncident] = useState<YesNoAll>('');

  // Applied filter states (what has been submitted to API) - initialize with last 8 weeks dates
  const [appliedFilters, setAppliedFilters] = useState<NOCDashboardFilters>({
    grouping: 'weekly',
    group_by: 'opened_at',
    start_date_opened: initialDates.start,
    end_date_opened: initialDates.end,
  });
  const [appliedCIClasses, setAppliedCIClasses] = useState<string[]>([]);
  const [appliedCloseCodes, setAppliedCloseCodes] = useState<string[]>([]);
  const [appliedOpenedBy, setAppliedOpenedBy] = useState<string[]>([]);
  const [appliedAssignmentGroups, setAppliedAssignmentGroups] = useState<string[]>([]);
  const [appliedLocations, setAppliedLocations] = useState<string[]>([]);
  const [appliedStates, setAppliedStates] = useState<string[]>([]);
  const [appliedVendorTicket, setAppliedVendorTicket] = useState<'' | 'yes' | 'no'>('');
  const [appliedResolvedBy, setAppliedResolvedBy] = useState<'' | 'NOC' | 'Others'>('');
  const [appliedDetectedByAlert, setAppliedDetectedByAlert] = useState<YesNoAll>('');
  const [appliedParentIncident, setAppliedParentIncident] = useState<YesNoAll>('');

  // Dirty flag: true when any filter (except grouping/group_by) has changed since last apply
  const [filtersDirty, setFiltersDirty] = useState(false);

  // View mode toggles
  const [view1Mode, setView1Mode] = useState<'count' | 'percentage'>('count');
  const [view3Mode, setView3Mode] = useState<'count' | 'percentage'>('count');
  const [view3aMode, setView3aMode] = useState<'count' | 'percentage'>('count');
  const [view3bMode, setView3bMode] = useState<'count' | 'percentage'>('count');
  const [view3cMode, setView3cMode] = useState<'count' | 'percentage'>('count');
  const [view4Mode, setView4Mode] = useState<'count' | 'percentage'>('count');
  const [view5Mode, setView5Mode] = useState<'count' | 'percentage'>('count');
  const [view6Mode, setView6Mode] = useState<'count' | 'percentage'>('count');

  // Popover anchors for period dropdowns
  const [openedPeriodAnchorEl, setOpenedPeriodAnchorEl] = useState<HTMLElement | null>(null);
  const [resolvedPeriodAnchorEl, setResolvedPeriodAnchorEl] = useState<HTMLElement | null>(null);
  // Calendar date inputs
  const [openedStartDateInput, setOpenedStartDateInput] = useState<string>('');
  const [openedEndDateInput, setOpenedEndDateInput] = useState<string>('');
  const [resolvedStartDateInput, setResolvedStartDateInput] = useState<string>('');
  const [resolvedEndDateInput, setResolvedEndDateInput] = useState<string>('');

  // Period preset dropdown selections (Last 8 weeks, Last 4 weeks, etc.)
  const [openedPeriodPreset, setOpenedPeriodPreset] = useState<'last8weeks' | 'last4weeks' | 'last2weeks' | 'last7days' | 'custom' | ''>('last8weeks');
  const [resolvedPeriodPreset, setResolvedPeriodPreset] = useState<'last8weeks' | 'last4weeks' | 'last2weeks' | 'last7days' | 'custom' | ''>('');

  // Expanded states for drill-downs in table headers
  const [showHoldReasons, setShowHoldReasons] = useState(false);
  const [showDestinations, setShowDestinations] = useState(false);

  // Sorting states for all tables
  const [table1SortColumn, setTable1SortColumn] = useState<string | null>(null);
  const [table1SortDirection, setTable1SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table2SortColumn, setTable2SortColumn] = useState<string | null>(null);
  const [table2SortDirection, setTable2SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table3SortColumn, setTable3SortColumn] = useState<string | null>('total');
  const [table3SortDirection, setTable3SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table4SortColumn, setTable4SortColumn] = useState<string | null>(null);
  const [table4SortDirection, setTable4SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table5SortColumn, setTable5SortColumn] = useState<string | null>(null);
  const [table5SortDirection, setTable5SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table6SortColumn, setTable6SortColumn] = useState<string | null>(null);
  const [table6SortDirection, setTable6SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table7SortColumn, setTable7SortColumn] = useState<string | null>(null);
  const [table7SortDirection, setTable7SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table8SortColumn, setTable8SortColumn] = useState<string | null>('total');
  const [table8SortDirection, setTable8SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table9SortColumn, setTable9SortColumn] = useState<string | null>('total');
  const [table9SortDirection, setTable9SortDirection] = useState<'asc' | 'desc'>('desc');
  const [table10SortColumn, setTable10SortColumn] = useState<string | null>('total');
  const [table10SortDirection, setTable10SortDirection] = useState<'asc' | 'desc'>('desc');

  // Filters panel collapse
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Active tab (Tabular or Visual or Raw Data)
  const [activeTab, setActiveTab] = useState<'tabular' | 'visual' | 'rawdata'>('tabular');

  // Raw Data states
  const [rawData, setRawData] = useState<RawIncidentRecord[]>([]);
  const [rawDataPagination, setRawDataPagination] = useState<RawDataPagination | null>(null);
  const [rawDataLoading, setRawDataLoading] = useState(false);
  const [rawDataError, setRawDataError] = useState<string | null>(null);
  const [rawDataPage, setRawDataPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [rawDataSortBy, setRawDataSortBy] = useState<string>('opened_at');
  const [rawDataSortDirection, setRawDataSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Applied raw data sorting states (what is actually used for API calls)
  const [appliedRawDataSortBy, setAppliedRawDataSortBy] = useState<string>('opened_at');
  const [appliedRawDataSortDirection, setAppliedRawDataSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination for large tables
  const [locationPage, setLocationPage] = useState(1);
  const [ciNamePage, setCINamePage] = useState(1);
  const rowsPerPage = 100;

  // formatLocalDate moved to top of file for state initialization

  // Helper function to get date from ISO week
  // ISO 8601: Week 1 is the week with the first Thursday, weeks start on Monday
  const getDateOfISOWeek = (week: number, year: number): Date => {
    // January 4th is always in week 1
    const jan4 = new Date(year, 0, 4);
    // Find the Monday of week 1
    const week1Monday = new Date(jan4);
    const dayOfWeek = jan4.getDay();
    // Calculate days to Monday (0=Sunday, 1=Monday, ..., 6=Saturday)
    const daysToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
    week1Monday.setDate(jan4.getDate() + daysToMonday);
    
    // Add (week - 1) weeks to get to the desired week
    const result = new Date(week1Monday);
    result.setDate(week1Monday.getDate() + (week - 1) * 7);
    
    return result;
  };

  // Helper function to sort options with "Blanks" at the top
  const sortOptionsWithBlanksFirst = (options: string[]): string[] => {
    return [...options].sort((a, b) => {
      const aIsBlanks = a.toLowerCase().includes('blank');
      const bIsBlanks = b.toLowerCase().includes('blank');
      
      if (aIsBlanks && !bIsBlanks) return -1;
      if (!aIsBlanks && bIsBlanks) return 1;
      return a.localeCompare(b);
    });
  };

  // Sorted filter options with Blanks at the top
  const sortedCIClassFilters = useMemo(() => 
    slicerOptions?.ciClasses ? sortOptionsWithBlanksFirst(slicerOptions.ciClasses) : [],
    [slicerOptions?.ciClasses]
  );

  const sortedCloseCodeFilters = useMemo(() => 
    slicerOptions?.closeCodes ? sortOptionsWithBlanksFirst(slicerOptions.closeCodes) : [],
    [slicerOptions?.closeCodes]
  );

  const sortedOpenedByFilters = useMemo(() => 
    slicerOptions?.openedBy ? sortOptionsWithBlanksFirst(slicerOptions.openedBy) : [],
    [slicerOptions?.openedBy]
  );

  const sortedAssignmentGroupFilters = useMemo(() => 
    slicerOptions?.assignmentGroups ? sortOptionsWithBlanksFirst(slicerOptions.assignmentGroups) : [],
    [slicerOptions?.assignmentGroups]
  );

  const sortedLocationFilters = useMemo(() => 
    slicerOptions?.locations ? sortOptionsWithBlanksFirst(slicerOptions.locations) : [],
    [slicerOptions?.locations]
  );

  const sortedStateFilters = useMemo(() => 
    slicerOptions?.states ? sortOptionsWithBlanksFirst(slicerOptions.states) : [],
    [slicerOptions?.states]
  );

  // Generate year options based on dateRange from slicerOptions
  const yearOptions = useMemo(() => {
    if (!slicerOptions?.dateRange) {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 6 }, (_, i) => currentYear - i);
    }
    // Use the broader range between opened_at and resolved_at
    const openedMin = slicerOptions.dateRange.opened_at?.min;
    const openedMax = slicerOptions.dateRange.opened_at?.max;
    const resolvedMin = slicerOptions.dateRange.resolved_at?.min;
    const resolvedMax = slicerOptions.dateRange.resolved_at?.max;
    
    const minYear = Math.min(
      openedMin ? new Date(openedMin).getFullYear() : Infinity,
      resolvedMin ? new Date(resolvedMin).getFullYear() : Infinity
    );
    const maxYear = Math.max(
      openedMax ? new Date(openedMax).getFullYear() : -Infinity,
      resolvedMax ? new Date(resolvedMax).getFullYear() : -Infinity
    );
    
    if (minYear === Infinity || maxYear === -Infinity) {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 6 }, (_, i) => currentYear - i);
    }
    
    const years = [];
    for (let year = maxYear; year >= minYear; year--) {
      years.push(year);
    }
    return years;
  }, [slicerOptions]);

  // Generate month options
  const allMonthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  // Filter month options based on selected year and dateRange for opened dates
  const getOpenedMonthOptions = useMemo(() => {
    if (!slicerOptions?.dateRange?.opened_at || !openedYear) return allMonthOptions;
    
    const minDate = new Date(slicerOptions.dateRange.opened_at.min);
    const maxDate = new Date(slicerOptions.dateRange.opened_at.max);
    const selectedYear = parseInt(openedYear);
    
    return allMonthOptions.filter(month => {
      const monthNum = parseInt(month.value);
      const yearMonth = new Date(selectedYear, monthNum - 1, 1);
      const yearMonthEnd = new Date(selectedYear, monthNum, 0);
      
      return yearMonthEnd >= minDate && yearMonth <= maxDate;
    });
  }, [slicerOptions, openedYear]);

  // Filter month options based on selected year and dateRange for resolved dates
  const getResolvedMonthOptions = useMemo(() => {
    if (!slicerOptions?.dateRange?.resolved_at || !resolvedYear) return allMonthOptions;
    
    const minDate = new Date(slicerOptions.dateRange.resolved_at.min);
    const maxDate = new Date(slicerOptions.dateRange.resolved_at.max);
    const selectedYear = parseInt(resolvedYear);
    
    return allMonthOptions.filter(month => {
      const monthNum = parseInt(month.value);
      const yearMonth = new Date(selectedYear, monthNum - 1, 1);
      const yearMonthEnd = new Date(selectedYear, monthNum, 0);
      
      return yearMonthEnd >= minDate && yearMonth <= maxDate;
    });
  }, [slicerOptions, resolvedYear]);

  // Generate week options (1-53) filtered by dateRange for opened dates
  const getOpenedWeekOptions = useMemo(() => {
    const allWeeks = Array.from({ length: 53 }, (_, i) => i + 1);
    if (!slicerOptions?.dateRange?.opened_at || !openedYear) return allWeeks;
    
    const minDate = new Date(slicerOptions.dateRange.opened_at.min);
    const maxDate = new Date(slicerOptions.dateRange.opened_at.max);
    const selectedYear = parseInt(openedYear);
    
    return allWeeks.filter(week => {
      const weekStart = getDateOfISOWeek(week, selectedYear);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      return weekEnd >= minDate && weekStart <= maxDate;
    });
  }, [slicerOptions, openedYear]);

  // Generate week options (1-53) filtered by dateRange for resolved dates
  const getResolvedWeekOptions = useMemo(() => {
    const allWeeks = Array.from({ length: 53 }, (_, i) => i + 1);
    if (!slicerOptions?.dateRange?.resolved_at || !resolvedYear) return allWeeks;
    
    const minDate = new Date(slicerOptions.dateRange.resolved_at.min);
    const maxDate = new Date(slicerOptions.dateRange.resolved_at.max);
    const selectedYear = parseInt(resolvedYear);
    
    return allWeeks.filter(week => {
      const weekStart = getDateOfISOWeek(week, selectedYear);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      return weekEnd >= minDate && weekStart <= maxDate;
    });
  }, [slicerOptions, resolvedYear]);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = process.env.REACT_APP_NOC_DASHBOARD_API;
      
      // Build request body with applied filters
      const requestBody: any = {
        grouping: appliedFilters.grouping,
        group_by: appliedFilters.group_by,
      };

      // Add date filters if present
      if (appliedFilters.start_date_opened) requestBody.start_date_opened = appliedFilters.start_date_opened;
      if (appliedFilters.end_date_opened) requestBody.end_date_opened = appliedFilters.end_date_opened;
      if (appliedFilters.start_date_resolved) requestBody.start_date_resolved = appliedFilters.start_date_resolved;
      if (appliedFilters.end_date_resolved) requestBody.end_date_resolved = appliedFilters.end_date_resolved;

      // Add multi-select filters as arrays - only if not all options are selected
      const allCIClasses = slicerOptions?.ciClasses || [];
      const allCloseCodes = slicerOptions?.closeCodes || [];
      const allOpenedBy = slicerOptions?.openedBy || [];
      const allAssignmentGroups = slicerOptions?.assignmentGroups || [];
      const allLocations = slicerOptions?.locations || [];
      const allStates = slicerOptions?.states || [];
      
      // Only send if not all options are selected (select all = send nothing)
      if (appliedCIClasses.length > 0 && appliedCIClasses.length < allCIClasses.length) {
        requestBody.ci_class = appliedCIClasses;
      }
      if (appliedCloseCodes.length > 0 && appliedCloseCodes.length < allCloseCodes.length) {
        requestBody.close_code = appliedCloseCodes;
      }
      if (appliedOpenedBy.length > 0 && appliedOpenedBy.length < allOpenedBy.length) {
        requestBody.opened_by = appliedOpenedBy;
      }
      if (appliedAssignmentGroups.length > 0 && appliedAssignmentGroups.length < allAssignmentGroups.length) {
        requestBody.assignment_group = appliedAssignmentGroups;
      }
      if (appliedLocations.length > 0 && appliedLocations.length < allLocations.length) {
        requestBody.location = appliedLocations;
      }
      if (appliedStates.length > 0 && appliedStates.length < allStates.length) {
        requestBody.state = appliedStates;
      }

      // Add vendor ticket filter only if set
      if (appliedVendorTicket !== '') requestBody.vendor_ticket = appliedVendorTicket;
      
      // Add resolved by filter only if set
      if (appliedResolvedBy === 'NOC') requestBody.resolved_by_noc = 'yes';
      if (appliedResolvedBy === 'Others') requestBody.resolved_by_noc = 'no';

      // Add boolean filters only if set (All => send nothing)
      if (appliedDetectedByAlert === 'yes') requestBody.detected_by_alert = true;
      if (appliedDetectedByAlert === 'no') requestBody.detected_by_alert = false;
      if (appliedParentIncident === 'yes') requestBody.has_parent_incident = true;
      if (appliedParentIncident === 'no') requestBody.has_parent_incident = false;

      const response = await authFetch(`${baseUrl}/noc-dashboard/dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      const result: APIResponse = await response.json();
      setData(result.data);
      setSlicerOptions(result.data.slicerOptions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Trigger data fetch when applied filters change or grouping/group_by changes
  // Only fetch dashboard data if NOT on raw data tab
  useEffect(() => {
    // Only fetch if we have the necessary auth data and not on raw data tab
    if (accessToken && graphData?.mail && activeTab !== 'rawdata') {
      fetchData();
    }
  }, [appliedFilters, appliedCIClasses, appliedCloseCodes, appliedOpenedBy, appliedAssignmentGroups, appliedLocations, appliedStates, appliedVendorTicket, appliedResolvedBy, appliedDetectedByAlert, appliedParentIncident, accessToken, graphData?.mail, activeTab]);

  // Fetch raw data for incidents
  const fetchRawData = async (page: number = 1) => {
    setRawDataLoading(true);
    setRawDataError(null);
    try {
      const baseUrl = process.env.REACT_APP_NOC_DASHBOARD_API;
      
      // Build request body with applied filters (same as main dashboard)
      const requestBody: any = {
        page: page,
        grouping: appliedFilters.grouping,
        group_by: appliedFilters.group_by,
      };

      // Add date filters if present
      if (appliedFilters.start_date_opened) requestBody.start_date_opened = appliedFilters.start_date_opened;
      if (appliedFilters.end_date_opened) requestBody.end_date_opened = appliedFilters.end_date_opened;
      if (appliedFilters.start_date_resolved) requestBody.start_date_resolved = appliedFilters.start_date_resolved;
      if (appliedFilters.end_date_resolved) requestBody.end_date_resolved = appliedFilters.end_date_resolved;

      // Add multi-select filters as arrays - only if not all options are selected
      const allCIClasses = slicerOptions?.ciClasses || [];
      const allCloseCodes = slicerOptions?.closeCodes || [];
      const allOpenedBy = slicerOptions?.openedBy || [];
      const allAssignmentGroups = slicerOptions?.assignmentGroups || [];
      const allLocations = slicerOptions?.locations || [];
      const allStates = slicerOptions?.states || [];
      
      // Only send if not all options are selected (select all = send nothing)
      if (appliedCIClasses.length > 0 && appliedCIClasses.length < allCIClasses.length) {
        requestBody.ci_class = appliedCIClasses;
      }
      if (appliedCloseCodes.length > 0 && appliedCloseCodes.length < allCloseCodes.length) {
        requestBody.close_code = appliedCloseCodes;
      }
      if (appliedOpenedBy.length > 0 && appliedOpenedBy.length < allOpenedBy.length) {
        requestBody.opened_by = appliedOpenedBy;
      }
      if (appliedAssignmentGroups.length > 0 && appliedAssignmentGroups.length < allAssignmentGroups.length) {
        requestBody.assignment_group = appliedAssignmentGroups;
      }
      if (appliedLocations.length > 0 && appliedLocations.length < allLocations.length) {
        requestBody.location = appliedLocations;
      }
      if (appliedStates.length > 0 && appliedStates.length < allStates.length) {
        requestBody.state = appliedStates;
      }

      // Add vendor ticket filter only if set
      if (appliedVendorTicket !== '') requestBody.vendor_ticket = appliedVendorTicket;
      
      // Add resolved by filter only if set
      if (appliedResolvedBy === 'NOC') requestBody.resolved_by_noc = 'yes';
      if (appliedResolvedBy === 'Others') requestBody.resolved_by_noc = 'no';

      // Add boolean filters only if set (All => send nothing)
      if (appliedDetectedByAlert === 'yes') requestBody.detected_by_alert = true;
      if (appliedDetectedByAlert === 'no') requestBody.detected_by_alert = false;
      if (appliedParentIncident === 'yes') requestBody.has_parent_incident = true;
      if (appliedParentIncident === 'no') requestBody.has_parent_incident = false;

      // Add sorting parameters
      requestBody.sort_by = appliedRawDataSortBy;
      requestBody.sort_direction = appliedRawDataSortDirection;

      const response = await authFetch(`${baseUrl}/noc-dashboard/incidents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error('Failed to fetch raw data');
      const result: RawDataResponse = await response.json();
      setRawData(result.data);
      setRawDataPagination(result.pagination);
    } catch (err) {
      setRawDataError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setRawDataLoading(false);
    }
  };

  // Initialization useEffect removed - dates are now initialized in state directly

  // Reset raw data page to 1 when applied sort parameters change
  useEffect(() => {
    if (activeTab === 'rawdata') {
      setRawDataPage(1);
    }
  }, [appliedRawDataSortBy, appliedRawDataSortDirection]);

  // Trigger raw data fetch when tab changes to raw data or page changes
  useEffect(() => {
    if (activeTab === 'rawdata' && accessToken && graphData?.mail) {
      fetchRawData(rawDataPage);
    }
  }, [activeTab, rawDataPage, appliedFilters, appliedCIClasses, appliedCloseCodes, appliedOpenedBy, appliedAssignmentGroups, appliedLocations, appliedStates, appliedVendorTicket, appliedResolvedBy, appliedDetectedByAlert, appliedParentIncident, appliedRawDataSortBy, appliedRawDataSortDirection, accessToken, graphData?.mail]);

  // Export raw data to CSV
  const exportRawData = async () => {
    setIsExporting(true);
    try {
      const baseUrl = process.env.REACT_APP_NOC_DASHBOARD_API;
      
      // Build request body with applied filters (same as main dashboard)
      const requestBody: any = {
        page_size: 1000,
        grouping: appliedFilters.grouping,
        group_by: appliedFilters.group_by,
      };

      // Add date filters if present
      if (appliedFilters.start_date_opened) requestBody.start_date_opened = appliedFilters.start_date_opened;
      if (appliedFilters.end_date_opened) requestBody.end_date_opened = appliedFilters.end_date_opened;
      if (appliedFilters.start_date_resolved) requestBody.start_date_resolved = appliedFilters.start_date_resolved;
      if (appliedFilters.end_date_resolved) requestBody.end_date_resolved = appliedFilters.end_date_resolved;

      // Add multi-select filters
      const allCIClasses = slicerOptions?.ciClasses || [];
      const allCloseCodes = slicerOptions?.closeCodes || [];
      const allOpenedBy = slicerOptions?.openedBy || [];
      const allAssignmentGroups = slicerOptions?.assignmentGroups || [];
      const allLocations = slicerOptions?.locations || [];
      const allStates = slicerOptions?.states || [];
      
      if (appliedCIClasses.length > 0 && appliedCIClasses.length < allCIClasses.length) {
        requestBody.ci_class = appliedCIClasses;
      }
      if (appliedCloseCodes.length > 0 && appliedCloseCodes.length < allCloseCodes.length) {
        requestBody.close_code = appliedCloseCodes;
      }
      if (appliedOpenedBy.length > 0 && appliedOpenedBy.length < allOpenedBy.length) {
        requestBody.opened_by = appliedOpenedBy;
      }
      if (appliedAssignmentGroups.length > 0 && appliedAssignmentGroups.length < allAssignmentGroups.length) {
        requestBody.assignment_group = appliedAssignmentGroups;
      }
      if (appliedLocations.length > 0 && appliedLocations.length < allLocations.length) {
        requestBody.location = appliedLocations;
      }
      if (appliedStates.length > 0 && appliedStates.length < allStates.length) {
        requestBody.state = appliedStates;
      }
      if (appliedVendorTicket !== '') requestBody.vendor_ticket = appliedVendorTicket;
      if (appliedResolvedBy === 'NOC') requestBody.resolved_by_noc = 'yes';
      if (appliedResolvedBy === 'Others') requestBody.resolved_by_noc = 'no';
      if (appliedDetectedByAlert === 'yes') requestBody.detected_by_alert = true;
      if (appliedDetectedByAlert === 'no') requestBody.detected_by_alert = false;
      if (appliedParentIncident === 'yes') requestBody.has_parent_incident = true;
      if (appliedParentIncident === 'no') requestBody.has_parent_incident = false;

      // Add sorting parameters
      requestBody.sort_by = rawDataSortBy;
      requestBody.sort_direction = rawDataSortDirection;

      // Fetch all pages in parallel
      const firstResponse = await authFetch(`${baseUrl}/noc-dashboard/incidents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...requestBody, page: 1 }),
      });

      if (!firstResponse.ok) throw new Error('Failed to fetch export data');
      const firstResult: RawDataResponse = await firstResponse.json();
      
      const allRecords: RawIncidentRecord[] = [...firstResult.data];
      const totalPages = firstResult.pagination.total_pages;
      const totalRecords = firstResult.pagination.total_records;

      console.log(`Export: Page 1/${totalPages} fetched, ${allRecords.length} records, Total expected: ${totalRecords}`);

      // Fetch remaining pages in parallel
      if (totalPages > 1) {
        const pagePromises = [];
        for (let page = 2; page <= totalPages; page++) {
          pagePromises.push(
            authFetch(`${baseUrl}/noc-dashboard/incidents`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ ...requestBody, page }),
            }).then(res => res.json())
          );
        }
        
        const results = await Promise.all(pagePromises);
        results.forEach((result: RawDataResponse, index) => {
          console.log(`Export: Page ${index + 2}/${totalPages} processing, ${result.data.length} records`);
          allRecords.push(...result.data);
        });
      }

      console.log(`Export: Total records collected: ${allRecords.length}, Expected: ${totalRecords}`);

      // Convert to CSV
      const headers = [
        'Incident ID',
        'Opened At',
        'Resolved At',
        'Assignment Group',
        'Close Code',
        'State',
        'Hold Reason',
        'SLA Assignment Groups',
        'CMDB CI',
        'Priority',
        'Vendor Name',
        'Category',
        'Subcategory',
        'Opened By',
        'CI Class',
        'CI Location',
        'MTTR Minutes',
      ];

      const rows = allRecords.map(record => [
        record.number,
        record.opened_at,
        record.resolved_at || '',
        record.assignment_group_dv,
        record.close_code || '',
        record.state_dv,
        record.hold_reason,
        record.u_sla_assignment_groups_dv,
        record.cmdb_ci_dv,
        record.priority,
        record.u_vendor_name_dv,
        record.category_dv,
        record.subcategory_dv,
        record.opened_by,
        record.ci_class,
        record.ci_location,
        record.mttr_minutes !== null ? record.mttr_minutes : '',
      ]);

      console.log(`Export: Creating CSV with ${rows.length} data rows`);
      exportToCSV(headers, rows, `noc_incidents_raw_data_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      setRawDataError(err instanceof Error ? err.message : 'An error occurred during export');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle period selection for opened dates
  const handleOpenedYearChange = (value: string) => {
    setOpenedYear(value);
    setOpenedMonth('');
    setOpenedWeek('');
    setOpenedStartDateInput('');
    setOpenedEndDateInput('');
    setOpenedPeriodPreset('');
    setFiltersDirty(true);
    if (value) {
      const start = `${value}-01-01`;
      const end = `${value}-12-31`;
      setFilters({ ...filters, start_date_opened: start, end_date_opened: end });
    } else {
      setFilters({ ...filters, start_date_opened: undefined, end_date_opened: undefined });
    }
  };

  const handleOpenedMonthChange = (value: string) => {
    setOpenedMonth(value);
    setOpenedWeek('');
    setOpenedStartDateInput('');
    setOpenedEndDateInput('');
    setOpenedPeriodPreset('');
    setOpenedPeriodAnchorEl(null);
    setFiltersDirty(true);
    if (value && openedYear) {
      const start = `${openedYear}-${value}-01`;
      const lastDay = new Date(parseInt(openedYear), parseInt(value), 0).getDate();
      const end = `${openedYear}-${value}-${lastDay.toString().padStart(2, '0')}`;
      setFilters({ ...filters, start_date_opened: start, end_date_opened: end });
    }
  };

  const handleOpenedWeekChange = (value: string) => {
    setOpenedWeek(value);
    setOpenedMonth('');
    setOpenedStartDateInput('');
    setOpenedEndDateInput('');
    setOpenedPeriodPreset('');
    setOpenedPeriodAnchorEl(null);
    setFiltersDirty(true);
    if (value && openedYear) {
      const start = getDateOfISOWeek(parseInt(value), parseInt(openedYear));
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      setFilters({
        ...filters,
        start_date_opened: formatLocalDate(start),
        end_date_opened: formatLocalDate(end),
      });
    }
  };

  // Handle period selection for resolved dates
  const handleResolvedYearChange = (value: string) => {
    setResolvedYear(value);
    setResolvedMonth('');
    setResolvedWeek('');
    setResolvedStartDateInput('');
    setResolvedEndDateInput('');
    setResolvedPeriodPreset('');
    setFiltersDirty(true);
    if (value) {
      const start = `${value}-01-01`;
      const end = `${value}-12-31`;
      setFilters({ ...filters, start_date_resolved: start, end_date_resolved: end });
    } else {
      setFilters({ ...filters, start_date_resolved: undefined, end_date_resolved: undefined });
    }
  };

  const handleResolvedMonthChange = (value: string) => {
    setResolvedMonth(value);
    setResolvedWeek('');
    setResolvedStartDateInput('');
    setResolvedEndDateInput('');
    setResolvedPeriodPreset('');
    setResolvedPeriodAnchorEl(null);
    setFiltersDirty(true);
    if (value && resolvedYear) {
      const start = `${resolvedYear}-${value}-01`;
      const lastDay = new Date(parseInt(resolvedYear), parseInt(value), 0).getDate();
      const end = `${resolvedYear}-${value}-${lastDay.toString().padStart(2, '0')}`;
      setFilters({ ...filters, start_date_resolved: start, end_date_resolved: end });
    }
  };

  const handleResolvedWeekChange = (value: string) => {
    setResolvedWeek(value);
    setResolvedMonth('');
    setResolvedStartDateInput('');
    setResolvedEndDateInput('');
    setResolvedPeriodPreset('');
    setResolvedPeriodAnchorEl(null);
    setFiltersDirty(true);
    if (value && resolvedYear) {
      const start = getDateOfISOWeek(parseInt(value), parseInt(resolvedYear));
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      setFilters({
        ...filters,
        start_date_resolved: formatLocalDate(start),
        end_date_resolved: formatLocalDate(end),
      });
    }
  };

  // Helper functions moved to top of file for state initialization

  // Handle opened period preset change
  const handleOpenedPeriodPresetChange = (value: 'last8weeks' | 'last4weeks' | 'last2weeks' | 'last7days' | 'custom' | '') => {
    setOpenedPeriodPreset(value);
    
    // Clear period selections
    setOpenedYear('');
    setOpenedMonth('');
    setOpenedWeek('');
    
    setFiltersDirty(true);
    
    if (value === '' || value === 'custom') {
      // For custom or empty, clear the date filters
      if (value === '') {
        setOpenedStartDateInput('');
        setOpenedEndDateInput('');
        setFilters({ ...filters, start_date_opened: undefined, end_date_opened: undefined });
      }
      // For custom, leave the current date inputs as they are
    } else {
      // Calculate dates for presets
      const dates = calculatePresetDates(value);
      setOpenedStartDateInput('');
      setOpenedEndDateInput('');
      setFilters({ ...filters, start_date_opened: dates.start, end_date_opened: dates.end });
    }
  };

  // Handle resolved period preset change
  const handleResolvedPeriodPresetChange = (value: 'last8weeks' | 'last4weeks' | 'last2weeks' | 'last7days' | 'custom' | '') => {
    setResolvedPeriodPreset(value);
    
    // Clear period selections
    setResolvedYear('');
    setResolvedMonth('');
    setResolvedWeek('');
    
    setFiltersDirty(true);
    
    if (value === '' || value === 'custom') {
      // For custom or empty, clear the date filters
      if (value === '') {
        setResolvedStartDateInput('');
        setResolvedEndDateInput('');
        setFilters({ ...filters, start_date_resolved: undefined, end_date_resolved: undefined });
      }
      // For custom, leave the current date inputs as they are
    } else {
      // Calculate dates for presets
      const dates = calculatePresetDates(value);
      setResolvedStartDateInput('');
      setResolvedEndDateInput('');
      setFilters({ ...filters, start_date_resolved: dates.start, end_date_resolved: dates.end });
    }
  };

  // Updated handlers for manual date inputs (for custom option)
  const handleOpenedStartDateChange = (value: string) => {
    setOpenedStartDateInput(value);
    setOpenedYear('');
    setOpenedMonth('');
    setOpenedWeek('');
    setOpenedPeriodPreset('custom');
    setFiltersDirty(true);
    setFilters({ ...filters, start_date_opened: value || undefined });
  };

  const handleOpenedEndDateChange = (value: string) => {
    setOpenedEndDateInput(value);
    setOpenedYear('');
    setOpenedMonth('');
    setOpenedWeek('');
    setOpenedPeriodPreset('custom');
    setFiltersDirty(true);
    setFilters({ ...filters, end_date_opened: value || undefined });
  };

  const handleResolvedStartDateChange = (value: string) => {
    setResolvedStartDateInput(value);
    setResolvedYear('');
    setResolvedMonth('');
    setResolvedWeek('');
    setResolvedPeriodPreset('custom');
    setFiltersDirty(true);
    setFilters({ ...filters, start_date_resolved: value || undefined });
  };

  const handleResolvedEndDateChange = (value: string) => {
    setResolvedEndDateInput(value);
    setResolvedYear('');
    setResolvedMonth('');
    setResolvedWeek('');
    setResolvedPeriodPreset('custom');
    setFiltersDirty(true);
    setFilters({ ...filters, end_date_resolved: value || undefined });
  };

  // Helper function to export table data to CSV
  const exportToCSV = (headers: string[], rows: (string | number)[][], filename: string) => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape cells containing commas or quotes
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // Helper function to handle column sorting
  const handleColumnSort = (
    column: string,
    currentSortColumn: string | null,
    currentSortDirection: 'asc' | 'desc',
    setSortColumn: (col: string | null) => void,
    setSortDirection: (dir: 'asc' | 'desc') => void
  ) => {
    if (currentSortColumn === column) {
      // Toggle direction
      setSortDirection(currentSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort handler for CI Location table (with page reset)
  const handleLocationSort = useCallback((column: string) => {
    setTable8SortColumn(prev => {
      if (prev === column) {
        setTable8SortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setTable8SortDirection('desc');
      return column;
    });
    setLocationPage(1);
  }, []);

  // Sort handler for CI Name table (with page reset)
  const handleCINameSort = useCallback((column: string) => {
    setTable9SortColumn(prev => {
      if (prev === column) {
        setTable9SortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setTable9SortDirection('desc');
      return column;
    });
    setCINamePage(1);
  }, []);

  // Stable sort handlers for all tables (useCallback with [] deps, using functional updaters)
  const handleTable1Sort = useCallback((column: string) => {
    setTable1SortColumn(prev => { if (prev === column) { setTable1SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable1SortDirection('desc'); return column; });
  }, []);
  const handleTable2Sort = useCallback((column: string) => {
    setTable2SortColumn(prev => { if (prev === column) { setTable2SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable2SortDirection('desc'); return column; });
  }, []);
  const handleTable3Sort = useCallback((column: string) => {
    setTable3SortColumn(prev => { if (prev === column) { setTable3SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable3SortDirection('desc'); return column; });
  }, []);
  const handleTable4Sort = useCallback((column: string) => {
    setTable4SortColumn(prev => { if (prev === column) { setTable4SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable4SortDirection('desc'); return column; });
  }, []);
  const handleTable5Sort = useCallback((column: string) => {
    setTable5SortColumn(prev => { if (prev === column) { setTable5SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable5SortDirection('desc'); return column; });
  }, []);
  const handleTable6Sort = useCallback((column: string) => {
    setTable6SortColumn(prev => { if (prev === column) { setTable6SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable6SortDirection('desc'); return column; });
  }, []);
  const handleTable7Sort = useCallback((column: string) => {
    setTable7SortColumn(prev => { if (prev === column) { setTable7SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable7SortDirection('desc'); return column; });
  }, []);
  const handleTable10Sort = useCallback((column: string) => {
    setTable10SortColumn(prev => { if (prev === column) { setTable10SortDirection(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setTable10SortDirection('desc'); return column; });
  }, []);

  // Helper component for sortable column headers
  // Export function for Incidents by Category table
  const exportIncidentsByCategory = () => {
    if (!data) return;
    const headers = ['Category', ...data.resolutionBuckets.map(b => b.period), 'Total'];
    // Use sorted rows from table1Rows
    const rows = table1Rows.map(row => {
      if (row.label === 'Managed by NOC (All)') {
        return [
          row.label,
          ...data.resolutionBuckets.map(bucket => bucket.managedByNOC),
          data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)
        ];
      } else if (row.label === 'Resolved by NOC') {
        return [
          row.label,
          ...data.resolutionBuckets.map(bucket => {
            return view1Mode === 'count' ? bucket.resolvedByNOC : bucket.managedByNOC > 0 ? `${((bucket.resolvedByNOC / bucket.managedByNOC) * 100).toFixed(1)}%` : '0.0%';
          }),
          view1Mode === 'count'
            ? data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByNOC, 0)
            : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 ? `${((data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByNOC, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100).toFixed(1)}%` : '0.0%'
        ];
      } else if (row.label === 'Resolved by Other') {
        return [
          row.label,
          ...data.resolutionBuckets.map(bucket => {
            return view1Mode === 'count' ? bucket.resolvedByOther : bucket.managedByNOC > 0 ? `${((bucket.resolvedByOther / bucket.managedByNOC) * 100).toFixed(1)}%` : '0.0%';
          }),
          view1Mode === 'count'
            ? data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByOther, 0)
            : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 ? `${((data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByOther, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100).toFixed(1)}%` : '0.0%'
        ];
      } else { // Cancelled
        return [
          row.label,
          ...data.resolutionBuckets.map(bucket => {
            return view1Mode === 'count' ? bucket.cancelled : bucket.managedByNOC > 0 ? `${((bucket.cancelled / bucket.managedByNOC) * 100).toFixed(1)}%` : '0.0%';
          }),
          view1Mode === 'count'
            ? data.resolutionBuckets.reduce((sum, b) => sum + b.cancelled, 0)
            : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 ? `${((data.resolutionBuckets.reduce((sum, b) => sum + b.cancelled, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100).toFixed(1)}%` : '0.0%'
        ];
      }
    });
    exportToCSV(headers, rows, 'incidents_by_category');
  };

  // Export function for MTTR by Category table
  const exportMTTRByCategory = () => {
    if (!data) return;
    const headers = ['Category', ...data.resolutionBuckets.map(b => b.period), 'Average'];
    // Use sorted rows from table2Rows
    const rows = table2Rows.map(row => {
      return [
        row.label,
        ...data.resolutionBuckets.map(b => row.getValue(b).toFixed(2)),
        (data.resolutionBuckets.reduce((sum, b) => sum + row.getValue(b), 0) / data.resolutionBuckets.length || 0).toFixed(2)
      ];
    });
    exportToCSV(headers, rows, 'mttr_by_category');
  };

  // Export function for Resolution Code Split table
  const exportResolutionCodeSplit = () => {
    if (!data) return;
    const headers = ['Resolution Code', ...data.resolutionCodeSplit.map(s => s.period), 'Total'];
    // Use sorted resolution codes
    const rows = sortedResolutionCodes.map(code => {
      const rowTotal = resolutionCodeTotals.rowTotals[code] || 0;
      const rowPercentage = resolutionCodeTotals.grandTotal > 0 ? (rowTotal / resolutionCodeTotals.grandTotal) * 100 : 0;
      return [
        code,
        ...data.resolutionCodeSplit.map(split => {
          const count = split.codes[code] || 0;
          const periodTotal = resolutionCodeTotals.periodTotals[split.period] || 0;
          const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
          return view3Mode === 'count' ? count : `${percentage.toFixed(1)}%`;
        }),
        view3Mode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`
      ];
    });
    // Add total row
    rows.push([
      'Total',
      ...data.resolutionCodeSplit.map(split => {
        const periodTotal = resolutionCodeTotals.periodTotals[split.period] || 0;
        return view3Mode === 'count' ? periodTotal : '100.0%';
      }),
      view3Mode === 'count' ? resolutionCodeTotals.grandTotal : '100.0%'
    ]);
    exportToCSV(headers, rows, 'incidents_by_resolution_code');
  };

  // Export function for Location Split table
  const exportLocationSplit = () => {
    if (!data) return;
    const headers = ['Location', ...data.locationSplit.map(s => s.period), 'Total'];
    // Use sorted locations
    const rows = sortedLocations.map(location => {
      const rowTotal = locationTotals.rowTotals[location] || 0;
      const rowPercentage = locationTotals.grandTotal > 0 ? (rowTotal / locationTotals.grandTotal) * 100 : 0;
      return [
        location,
        ...data.locationSplit.map(split => {
          const count = split.locations[location] || 0;
          const periodTotal = locationTotals.periodTotals[split.period] || 0;
          const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
          return view3aMode === 'count' ? count : `${percentage.toFixed(1)}%`;
        }),
        view3aMode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`
      ];
    });
    // Add total row
    rows.push([
      'Total',
      ...data.locationSplit.map(split => {
        const periodTotal = locationTotals.periodTotals[split.period] || 0;
        return view3aMode === 'count' ? periodTotal : '100.0%';
      }),
      view3aMode === 'count' ? locationTotals.grandTotal : '100.0%'
    ]);
    exportToCSV(headers, rows, 'incidents_by_ci_location');
  };

  // Export function for MTTR Buckets table
  const exportMTTRBuckets = () => {
    if (!data) return;
    const headers = ['MTTR Bucket', ...data.mttrBuckets.map(b => b.period), 'Total'];
    // Use sorted MTTR buckets
    const rows = sortedMTTRBuckets.map(bucketName => {
      const rowTotal = data.mttrBuckets.reduce((sum, b) => sum + b.buckets[bucketName as keyof typeof b.buckets], 0);
      const grandTotal = data.mttrBuckets.reduce((sum, b) => sum + Object.values(b.buckets).reduce((s, val) => s + val, 0), 0);
      const rowPercentage = grandTotal > 0 ? (rowTotal / grandTotal) * 100 : 0;
      return [
        bucketName,
        ...data.mttrBuckets.map(bucket => {
          const count = bucket.buckets[bucketName as keyof typeof bucket.buckets];
          const total = Object.values(bucket.buckets).reduce((sum, val) => sum + val, 0);
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return view4Mode === 'count' ? count : `${percentage.toFixed(1)}%`;
        }),
        view4Mode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`
      ];
    });
    exportToCSV(headers, rows, 'incidents_by_mttr_buckets');
  };

  // Export function for State table
  const exportStateAndHoldReasons = () => {
    if (!data) return;
    const headers = ['State', ...data.stateAndHoldReasons.map(i => i.period), 'Total'];
    const rows: (string | number)[][] = [];
    // Use sorted states
    sortedStates.forEach(state => {
      const rowTotal = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons[state]?.count || 0), 0);
      const grandTotal = data.stateAndHoldReasons.reduce((sum, item) => sum + Object.values(item.stateReasons).reduce((s, sr) => s + sr.count, 0), 0);
      const rowPercentage = grandTotal > 0 ? (rowTotal / grandTotal) * 100 : 0;
      rows.push([
        state,
        ...data.stateAndHoldReasons.map(item => {
          const count = item.stateReasons[state]?.count || 0;
          const total = Object.values(item.stateReasons).reduce((sum, s) => sum + s.count, 0);
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return view5Mode === 'count' ? count : `${percentage.toFixed(1)}%`;
        }),
        view5Mode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`
      ]);
      // Add hold reasons for On Hold state using sorted hold reasons
      if (state === 'On Hold' && sortedHoldReasons.length > 0) {
        sortedHoldReasons.forEach(reason => {
          const totalReasonCount = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons['On Hold']?.holdReasons[reason] || 0), 0);
          const totalOnHold = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons['On Hold']?.count || 0), 0);
          const totalPercentage = totalOnHold > 0 ? (totalReasonCount / totalOnHold) * 100 : 0;
          rows.push([
            `  ${reason}`,
            ...data.stateAndHoldReasons.map(item => {
              const count = item.stateReasons['On Hold']?.holdReasons[reason] || 0;
              const totalOnHoldForPeriod = item.stateReasons['On Hold']?.count || 0;
              const percentage = totalOnHoldForPeriod > 0 ? (count / totalOnHoldForPeriod) * 100 : 0;
              return view5Mode === 'count' ? count : `${percentage.toFixed(1)}%`;
            }),
            view5Mode === 'count' ? totalReasonCount : `${totalPercentage.toFixed(1)}%`
          ]);
        });
      }
    });
    // Add total row
    rows.push([
      'Total',
      ...data.stateAndHoldReasons.map(item => {
        const periodTotal = Object.values(item.stateReasons).reduce((sum, sr) => sum + sr.count, 0);
        return view5Mode === 'count' ? periodTotal : '100.0%';
      }),
      view5Mode === 'count' ? data.stateAndHoldReasons.reduce((sum, item) => sum + Object.values(item.stateReasons).reduce((s, sr) => s + sr.count, 0), 0) : '100.0%'
    ]);
    exportToCSV(headers, rows, 'incidents_by_state');
  };

  // Export function for NOC Transfers table
  const exportNOCTransfers = () => {
    if (!data) return;
    const headers = ['Metric', ...data.nocTransfers.timeSeries.map(t => t.period), 'Total'];
    // Use sorted rows from table6Rows
    const rows: (string | number)[][] = table6Rows.map(row => {
      if (row.label === 'Managed by NOC (All)') {
        return ['Managed by NOC', ...data.resolutionBuckets.map(t => t.managedByNOC), data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)];
      } else if (row.label === 'Started at NOC') {
        return ['Started at NOC', ...data.nocTransfers.timeSeries.map((t, index) => {
          const managedByNOC = data.resolutionBuckets[index]?.managedByNOC || 0;
          return view6Mode === 'count' ? t.startedAtNOC : managedByNOC > 0 ? `${((t.startedAtNOC / managedByNOC) * 100).toFixed(1)}%` : '0.0%';
        }), (() => {
          const totalManagedByNOC = data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0);
          return view6Mode === 'count' ? data.nocTransfers.total : totalManagedByNOC > 0 ? `${((data.nocTransfers.total / totalManagedByNOC) * 100).toFixed(1)}%` : '0.0%';
        })()];
      } else if (row.label === 'Solved by automation') {
        return ['Solved by automation', ...data.nocTransfers.timeSeries.map((t, index) => {
          const managedByNOC = data.resolutionBuckets[index]?.managedByNOC || 0;
          return view6Mode === 'count' ? t.solvedByAutomation : managedByNOC > 0 ? `${((t.solvedByAutomation / managedByNOC) * 100).toFixed(1)}%` : '0.0%';
        }), (() => {
          const totalManagedByNOC = data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0);
          return view6Mode === 'count' ? data.nocTransfers.totalSolvedByAutomation : totalManagedByNOC > 0 ? `${((data.nocTransfers.totalSolvedByAutomation / totalManagedByNOC) * 100).toFixed(1)}%` : '0.0%';
        })()];
      } else { // Sent Out
        return ['Sent Out', ...data.nocTransfers.timeSeries.map((t, index) => {
          const managedByNOC = data.resolutionBuckets[index]?.managedByNOC || 0;
          return view6Mode === 'count' ? t.sentOut : managedByNOC > 0 ? `${((t.sentOut / managedByNOC) * 100).toFixed(1)}%` : '0.0%';
        }), (() => {
          const totalManagedByNOC = data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0);
          return view6Mode === 'count' ? data.nocTransfers.totalSentOut : totalManagedByNOC > 0 ? `${((data.nocTransfers.totalSentOut / totalManagedByNOC) * 100).toFixed(1)}%` : '0.0%';
        })()];
      }
    });
    // Add destinations using sorted destinations
    if (sortedDestinations.length > 0) {
      // Create a map from period to destinations data
      const destMap = new Map<string, any>();
      data.transferDestinations.timeSeries.forEach((transfer) => {
        destMap.set(transfer.period, transfer.destinations);
      });

      sortedDestinations.forEach(dest => {
        const destOverall = data.transferDestinations.overallDestinations[dest];
        const count = destOverall?.count || 0;
        rows.push([
          `  ${dest}`,
          ...data.nocTransfers.timeSeries.map(transfer => {
            const periodDests = destMap.get(transfer.period);
            const destCount = periodDests?.[dest]?.count || 0;
            return view6Mode === 'count' ? destCount : transfer.sentOut > 0 ? `${((destCount / transfer.sentOut) * 100).toFixed(1)}%` : '0.0%';
          }),
          view6Mode === 'count' ? count : data.nocTransfers.totalSentOut > 0 ? `${((count / data.nocTransfers.totalSentOut) * 100).toFixed(1)}%` : '0.0%'
        ]);
      });
    }
    exportToCSV(headers, rows, 'incident_transfers');
  };

  // Export function for OBS Incidents table
  const exportOBSIncidents = () => {
    if (!data) return;
    const headers = ['Metric', ...data.obsIncidents.timeSeries.map(o => o.period), 'Total'];
    const rows = [
      ['OBS Count', ...data.obsIncidents.timeSeries.map(o => o.count), data.obsIncidents.total]
    ];
    exportToCSV(headers, rows, 'incidents_sent_to_obs');
  };

  // Export function for CI Name Split table
  const exportCINameSplit = () => {
    if (!data) return;
    const headers = ['CI Name', ...data.ciNameSplit.map(s => s.period), 'Total'];
    // Use sorted CI names
    const rows = sortedCINames.map(ciName => {
      const rowTotal = ciNameTotals.rowTotals[ciName] || 0;
      const rowPercentage = ciNameTotals.grandTotal > 0 ? (rowTotal / ciNameTotals.grandTotal) * 100 : 0;
      return [
        ciName,
        ...data.ciNameSplit.map(split => {
          const count = split.ciNames[ciName] || 0;
          const periodTotal = ciNameTotals.periodTotals[split.period] || 0;
          const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
          return view3bMode === 'count' ? count : `${percentage.toFixed(1)}%`;
        }),
        view3bMode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`
      ];
    });
    // Add total row
    rows.push([
      'Total',
      ...data.ciNameSplit.map(split => {
        const periodTotal = ciNameTotals.periodTotals[split.period] || 0;
        return view3bMode === 'count' ? periodTotal : '100.0%';
      }),
      view3bMode === 'count' ? ciNameTotals.grandTotal : '100.0%'
    ]);
    exportToCSV(headers, rows, 'incidents_by_ci_name');
  };

  // Export function for CI Class Split table
  const exportCIClassSplit = () => {
    if (!data) return;
    const headers = ['CI Class', ...data.ciClassSplit.map(s => s.period), 'Total'];
    // Use sorted CI classes
    const rows = sortedCIClasses.map(ciClass => {
      const rowTotal = ciClassTotals.rowTotals[ciClass] || 0;
      const rowPercentage = ciClassTotals.grandTotal > 0 ? (rowTotal / ciClassTotals.grandTotal) * 100 : 0;
      return [
        ciClass,
        ...data.ciClassSplit.map(split => {
          const count = split.ciClasses[ciClass] || 0;
          const periodTotal = ciClassTotals.periodTotals[split.period] || 0;
          const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
          return view3cMode === 'count' ? count : `${percentage.toFixed(1)}%`;
        }),
        view3cMode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`
      ];
    });
    // Add total row
    rows.push([
      'Total',
      ...data.ciClassSplit.map(split => {
        const periodTotal = ciClassTotals.periodTotals[split.period] || 0;
        return view3cMode === 'count' ? periodTotal : '100.0%';
      }),
      view3cMode === 'count' ? ciClassTotals.grandTotal : '100.0%'
    ]);
    exportToCSV(headers, rows, 'incidents_by_ci_class');
  };

  // Clear opened date filters
  const clearOpenedDates = () => {
    setFilters({
      ...filters,
      start_date_opened: undefined,
      end_date_opened: undefined,
    });
    setOpenedYear('');
    setOpenedMonth('');
    setOpenedWeek('');
    setOpenedStartDateInput('');
    setOpenedEndDateInput('');
    setOpenedPeriodPreset('');
    setFiltersDirty(true);
  };

  // Clear resolved date filters
  const clearResolvedDates = () => {
    setFilters({
      ...filters,
      start_date_resolved: undefined,
      end_date_resolved: undefined,
    });
    setResolvedYear('');
    setResolvedMonth('');
    setResolvedWeek('');
    setResolvedStartDateInput('');
    setResolvedEndDateInput('');
    setResolvedPeriodPreset('');
    setFiltersDirty(true);
  };

  // Apply raw data sorting
  const applyRawDataSorting = () => {
    setAppliedRawDataSortBy(rawDataSortBy);
    setAppliedRawDataSortDirection(rawDataSortDirection);
  };

  // Clear all filters
  const clearAllFilters = () => {
    const resetFilters = {
      grouping: 'weekly' as const,
      group_by: 'opened_at' as const,
      start_date_opened: undefined,
      end_date_opened: undefined,
      start_date_resolved: undefined,
      end_date_resolved: undefined,
    };
    
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setSelectedCIClasses([]);
    setAppliedCIClasses([]);
    setSelectedCloseCodes([]);
    setAppliedCloseCodes([]);
    setSelectedOpenedBy([]);
    setAppliedOpenedBy([]);
    setSelectedAssignmentGroups([]);
    setAppliedAssignmentGroups([]);
    setSelectedLocations([]);
    setAppliedLocations([]);
    setSelectedStates([]);
    setAppliedStates([]);
    setVendorTicket('');
    setAppliedVendorTicket('');
    setResolvedBy('');
    setAppliedResolvedBy('');
    setDetectedByAlert('');
    setAppliedDetectedByAlert('');
    setParentIncident('');
    setAppliedParentIncident('');
    setFiltersDirty(false);
    // Clear UI state for period selectors
    setOpenedYear('');
    setOpenedMonth('');
    setOpenedWeek('');
    setOpenedStartDateInput('');
    setOpenedEndDateInput('');
    setOpenedPeriodPreset('');
    setResolvedYear('');
    setResolvedMonth('');
    setResolvedWeek('');
    setResolvedStartDateInput('');
    setResolvedEndDateInput('');
    setResolvedPeriodPreset('');
  };

  // Apply filters handler
  const handleApplyFilters = () => {
    // Copy all current filter values to applied state
    setAppliedFilters({ ...filters });
    setAppliedCIClasses([...selectedCIClasses]);
    setAppliedCloseCodes([...selectedCloseCodes]);
    setAppliedOpenedBy([...selectedOpenedBy]);
    setAppliedAssignmentGroups([...selectedAssignmentGroups]);
    setAppliedLocations([...selectedLocations]);
    setAppliedStates([...selectedStates]);
    setAppliedVendorTicket(vendorTicket);
    setAppliedResolvedBy(resolvedBy);
    setAppliedDetectedByAlert(detectedByAlert);
    setAppliedParentIncident(parentIncident);
    setFiltersDirty(false);
    // Reset raw data page to 1 when filters change
    setRawDataPage(1);
  };

  // Refresh handler - fetches appropriate data based on active tab
  const handleRefresh = () => {
    if (activeTab === 'rawdata') {
      fetchRawData(rawDataPage);
    } else {
      fetchData();
    }
  };

  // Get all unique resolution codes across all periods
  const allResolutionCodes = useMemo(() => {
    if (!data) return [];
    const codes = new Set<string>();
    data.resolutionCodeSplit.forEach((split) => {
      Object.keys(split.codes).forEach((code) => codes.add(code));
    });
    return Array.from(codes).sort();
  }, [data]);

  // Get all unique locations across all periods
  const allLocations = useMemo(() => {
    if (!data) return [];
    const locations = new Set<string>();
    data.locationSplit.forEach((split) => {
      Object.keys(split.locations).forEach((location) => locations.add(location));
    });
    return Array.from(locations).sort();
  }, [data]);

  // Get all unique CI names across all periods
  const allCINames = useMemo(() => {
    if (!data) return [];
    const ciNames = new Set<string>();
    data.ciNameSplit.forEach((split) => {
      Object.keys(split.ciNames).forEach((name) => ciNames.add(name));
    });
    return Array.from(ciNames).sort();
  }, [data]);

  // Get all unique CI classes across all periods
  const allCIClasses = useMemo(() => {
    if (!data) return [];
    const ciClasses = new Set<string>();
    data.ciClassSplit.forEach((split) => {
      Object.keys(split.ciClasses).forEach((cls) => ciClasses.add(cls));
    });
    return Array.from(ciClasses).sort();
  }, [data]);

  // Get all unique states across all periods
  const allStates = useMemo(() => {
    if (!data) return [];
    const states = new Set<string>();
    data.stateAndHoldReasons.forEach((item) => {
      Object.keys(item.stateReasons).forEach((state) => states.add(state));
    });
    return Array.from(states).sort();
  }, [data]);

  // Get all hold reasons for On Hold state
  const allHoldReasons = useMemo(() => {
    if (!data) return [];
    const reasons = new Set<string>();
    data.stateAndHoldReasons.forEach((item) => {
      if (item.stateReasons['On Hold']) {
        Object.keys(item.stateReasons['On Hold'].holdReasons).forEach((reason) => reasons.add(reason));
      }
    });
    return Array.from(reasons).sort();
  }, [data]);

  // Get all transfer destinations
  const allDestinations = useMemo(() => {
    if (!data) return [];
    const destinations = new Set<string>();
    data.transferDestinations.timeSeries.forEach((item) => {
      Object.keys(item.destinations).forEach((dest) => destinations.add(dest));
    });
    return Array.from(destinations).sort();
  }, [data]);

  // Sorted data for Table 3: Resolution Codes
  const sortedResolutionCodes = useMemo(() => {
    if (!table3SortColumn || !data) return allResolutionCodes;
    
    const codesWithValues = allResolutionCodes.map(code => {
      let value = 0;
      if (table3SortColumn === 'total') {
        value = data.resolutionCodeSplit.reduce((sum, split) => sum + (split.codes[code] || 0), 0);
      } else {
        const split = data.resolutionCodeSplit.find(s => s.period === table3SortColumn);
        value = split?.codes[code] || 0;
      }
      return { code, value };
    });
    
    codesWithValues.sort((a, b) => {
      return table3SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return codesWithValues.map(item => item.code);
  }, [allResolutionCodes, table3SortColumn, table3SortDirection, data]);

  // Sorted data for Table 4: MTTR Buckets
  const sortedMTTRBuckets = useMemo(() => {
    if (!table4SortColumn || !data) return ['0-10 mins', '10-30 mins', '30 mins - 24 hours', '24 - 48 hours', '48 - 72 hours', '>72 hours'];
    
    const bucketsWithValues = ['0-10 mins', '10-30 mins', '30 mins - 24 hours', '24 - 48 hours', '48 - 72 hours', '>72 hours'].map(bucketName => {
      let value = 0;
      if (table4SortColumn === 'total') {
        value = data.mttrBuckets.reduce((sum, b) => sum + b.buckets[bucketName as keyof typeof b.buckets], 0);
      } else {
        const bucket = data.mttrBuckets.find(b => b.period === table4SortColumn);
        value = bucket?.buckets[bucketName as keyof typeof bucket.buckets] || 0;
      }
      return { bucketName, value };
    });
    
    bucketsWithValues.sort((a, b) => {
      return table4SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return bucketsWithValues.map(item => item.bucketName);
  }, [table4SortColumn, table4SortDirection, data]);

  // Sorted data for Table 5: States
  const sortedStates = useMemo(() => {
    if (!table5SortColumn || !data) return allStates;
    
    const statesWithValues = allStates.map(state => {
      let value = 0;
      if (table5SortColumn === 'total') {
        value = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons[state]?.count || 0), 0);
      } else {
        const item = data.stateAndHoldReasons.find(i => i.period === table5SortColumn);
        value = item?.stateReasons[state]?.count || 0;
      }
      return { state, value };
    });
    
    statesWithValues.sort((a, b) => {
      return table5SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return statesWithValues.map(item => item.state);
  }, [allStates, table5SortColumn, table5SortDirection, data]);

  // Sorted data for Table 5: Hold Reasons (nested under On Hold)
  const sortedHoldReasons = useMemo(() => {
    if (!table5SortColumn || !data) return allHoldReasons;
    
    const reasonsWithValues = allHoldReasons.map(reason => {
      let value = 0;
      if (table5SortColumn === 'total') {
        value = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons['On Hold']?.holdReasons[reason] || 0), 0);
      } else {
        const item = data.stateAndHoldReasons.find(i => i.period === table5SortColumn);
        value = item?.stateReasons['On Hold']?.holdReasons[reason] || 0;
      }
      return { reason, value };
    });
    
    reasonsWithValues.sort((a, b) => {
      return table5SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return reasonsWithValues.map(item => item.reason);
  }, [allHoldReasons, table5SortColumn, table5SortDirection, data]);

  // Sorted data for Table 6: Destinations (nested)
  const sortedDestinations = useMemo(() => {
    if (!table6SortColumn || !data) return allDestinations;
    
    const destinationsWithValues = allDestinations.map(dest => {
      let value = 0;
      if (table6SortColumn === 'total') {
        const destOverall = data.transferDestinations.overallDestinations[dest];
        value = destOverall?.count || 0;
      } else {
        const transfer = data.transferDestinations.timeSeries.find(t => t.period === table6SortColumn);
        const destInfo = transfer?.destinations[dest];
        value = destInfo?.count || 0;
      }
      return { dest, value };
    });
    
    destinationsWithValues.sort((a, b) => {
      return table6SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return destinationsWithValues.map(item => item.dest);
  }, [allDestinations, table6SortColumn, table6SortDirection, data]);

  // Sorted data for Table 8: Locations
  const sortedLocations = useMemo(() => {
    if (!table8SortColumn || !data) return allLocations;
    
    const locationsWithValues = allLocations.map(location => {
      let value = 0;
      if (table8SortColumn === 'total') {
        value = data.locationSplit.reduce((sum, split) => sum + (split.locations[location] || 0), 0);
      } else {
        const split = data.locationSplit.find(s => s.period === table8SortColumn);
        value = split?.locations[location] || 0;
      }
      return { location, value };
    });
    
    locationsWithValues.sort((a, b) => {
      return table8SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return locationsWithValues.map(item => item.location);
  }, [allLocations, table8SortColumn, table8SortDirection, data]);

  // Sorted data for Table 9: CI Names
  const sortedCINames = useMemo(() => {
    if (!table9SortColumn || !data) return allCINames;
    
    const ciNamesWithValues = allCINames.map(ciName => {
      let value = 0;
      if (table9SortColumn === 'total') {
        value = data.ciNameSplit.reduce((sum, split) => sum + (split.ciNames[ciName] || 0), 0);
      } else {
        const split = data.ciNameSplit.find(s => s.period === table9SortColumn);
        value = split?.ciNames[ciName] || 0;
      }
      return { ciName, value };
    });
    
    ciNamesWithValues.sort((a, b) => {
      return table9SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return ciNamesWithValues.map(item => item.ciName);
  }, [allCINames, table9SortColumn, table9SortDirection, data]);

  // Sorted data for Table 10: CI Classes
  const sortedCIClasses = useMemo(() => {
    if (!table10SortColumn || !data) return allCIClasses;
    
    const ciClassesWithValues = allCIClasses.map(ciClass => {
      let value = 0;
      if (table10SortColumn === 'total') {
        value = data.ciClassSplit.reduce((sum, split) => sum + (split.ciClasses[ciClass] || 0), 0);
      } else {
        const split = data.ciClassSplit.find(s => s.period === table10SortColumn);
        value = split?.ciClasses[ciClass] || 0;
      }
      return { ciClass, value };
    });
    
    ciClassesWithValues.sort((a, b) => {
      return table10SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return ciClassesWithValues.map(item => item.ciClass);
  }, [allCIClasses, table10SortColumn, table10SortDirection, data]);

  // Pre-computed totals to avoid recalculating in every row render (O(n²) -> O(n))
  const resolutionCodeTotals = useMemo(() => {
    if (!data) return { grandTotal: 0, rowTotals: {} as Record<string, number>, periodTotals: {} as Record<string, number> };
    const rowTotals: Record<string, number> = {};
    const periodTotals: Record<string, number> = {};
    let grandTotal = 0;
    data.resolutionCodeSplit.forEach((split) => {
      let periodSum = 0;
      Object.entries(split.codes).forEach(([code, count]) => {
        rowTotals[code] = (rowTotals[code] || 0) + count;
        periodSum += count;
      });
      periodTotals[split.period] = periodSum;
      grandTotal += periodSum;
    });
    return { grandTotal, rowTotals, periodTotals };
  }, [data]);

  const locationTotals = useMemo(() => {
    if (!data) return { grandTotal: 0, rowTotals: {} as Record<string, number>, periodTotals: {} as Record<string, number> };
    const rowTotals: Record<string, number> = {};
    const periodTotals: Record<string, number> = {};
    let grandTotal = 0;
    data.locationSplit.forEach((split) => {
      let periodSum = 0;
      Object.entries(split.locations).forEach(([loc, count]) => {
        rowTotals[loc] = (rowTotals[loc] || 0) + count;
        periodSum += count;
      });
      periodTotals[split.period] = periodSum;
      grandTotal += periodSum;
    });
    return { grandTotal, rowTotals, periodTotals };
  }, [data]);

  const ciNameTotals = useMemo(() => {
    if (!data) return { grandTotal: 0, rowTotals: {} as Record<string, number>, periodTotals: {} as Record<string, number> };
    const rowTotals: Record<string, number> = {};
    const periodTotals: Record<string, number> = {};
    let grandTotal = 0;
    data.ciNameSplit.forEach((split) => {
      let periodSum = 0;
      Object.entries(split.ciNames).forEach(([name, count]) => {
        rowTotals[name] = (rowTotals[name] || 0) + count;
        periodSum += count;
      });
      periodTotals[split.period] = periodSum;
      grandTotal += periodSum;
    });
    return { grandTotal, rowTotals, periodTotals };
  }, [data]);

  const ciClassTotals = useMemo(() => {
    if (!data) return { grandTotal: 0, rowTotals: {} as Record<string, number>, periodTotals: {} as Record<string, number> };
    const rowTotals: Record<string, number> = {};
    const periodTotals: Record<string, number> = {};
    let grandTotal = 0;
    data.ciClassSplit.forEach((split) => {
      let periodSum = 0;
      Object.entries(split.ciClasses).forEach(([cls, count]) => {
        rowTotals[cls] = (rowTotals[cls] || 0) + count;
        periodSum += count;
      });
      periodTotals[split.period] = periodSum;
      grandTotal += periodSum;
    });
    return { grandTotal, rowTotals, periodTotals };
  }, [data]);

  // Sorted rows for Table 1: Incidents by Category
  const table1Rows = useMemo(() => {
    const rows = [
      { label: 'Managed by NOC (All)', getValue: (bucket: ResolutionBucket) => bucket.managedByNOC },
      { label: 'Resolved by NOC', getValue: (bucket: ResolutionBucket) => view1Mode === 'count' ? bucket.resolvedByNOC : (bucket.managedByNOC > 0 ? (bucket.resolvedByNOC / bucket.managedByNOC) * 100 : 0) },
      { label: 'Resolved by Other', getValue: (bucket: ResolutionBucket) => view1Mode === 'count' ? bucket.resolvedByOther : (bucket.managedByNOC > 0 ? (bucket.resolvedByOther / bucket.managedByNOC) * 100 : 0) },
      { label: 'Cancelled', getValue: (bucket: ResolutionBucket) => view1Mode === 'count' ? bucket.cancelled : (bucket.managedByNOC > 0 ? (bucket.cancelled / bucket.managedByNOC) * 100 : 0) }
    ];
    
    if (!table1SortColumn || !data) return rows;
    
    const rowsWithValues = rows.map(row => {
      let value = 0;
      if (table1SortColumn === 'total') {
        if (row.label === 'Managed by NOC (All)') {
          value = data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0);
        } else if (row.label === 'Resolved by NOC') {
          value = view1Mode === 'count' 
            ? data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByNOC, 0)
            : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 
              ? (data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByNOC, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100 
              : 0;
        } else if (row.label === 'Resolved by Other') {
          value = view1Mode === 'count' 
            ? data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByOther, 0)
            : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 
              ? (data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByOther, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100 
              : 0;
        } else if (row.label === 'Cancelled') {
          value = view1Mode === 'count' 
            ? data.resolutionBuckets.reduce((sum, b) => sum + b.cancelled, 0)
            : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 
              ? (data.resolutionBuckets.reduce((sum, b) => sum + b.cancelled, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100 
              : 0;
        }
      } else {
        const bucket = data.resolutionBuckets.find(b => b.period === table1SortColumn);
        if (bucket) {
          value = row.getValue(bucket);
        }
      }
      return { ...row, value };
    });
    
    rowsWithValues.sort((a, b) => {
      return table1SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return rowsWithValues;
  }, [data, table1SortColumn, table1SortDirection, view1Mode]);

  // Sorted rows for Table 2: MTTR by Category
  const table2Rows = useMemo(() => {
    const rows = [
      { label: 'Managed by NOC (All)', getValue: (bucket: ResolutionBucket) => bucket.mttrManagedByNOC || 0 },
      { label: 'Resolved by NOC', getValue: (bucket: ResolutionBucket) => bucket.mttrResolvedByNOC || 0 },
      { label: 'Resolved by Other', getValue: (bucket: ResolutionBucket) => bucket.mttrResolvedByOther || 0 },
      { label: 'Cancelled', getValue: (bucket: ResolutionBucket) => bucket.mttrCancelled || 0 },
      { label: 'P90 TTR (Resolved by NOC)', getValue: (bucket: ResolutionBucket) => bucket.p90TTR || 0 }
    ];
    
    if (!table2SortColumn || !data) return rows;
    
    const rowsWithValues = rows.map(row => {
      let value = 0;
      if (table2SortColumn === 'total') {
        value = data.resolutionBuckets.reduce((sum, b) => sum + row.getValue(b), 0) / data.resolutionBuckets.length;
      } else {
        const bucket = data.resolutionBuckets.find(b => b.period === table2SortColumn);
        if (bucket) {
          value = row.getValue(bucket);
        }
      }
      return { ...row, value };
    });
    
    rowsWithValues.sort((a, b) => {
      return table2SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return rowsWithValues;
  }, [data, table2SortColumn, table2SortDirection]);

  // Sorted rows for Table 6: NOC Transfers (only the expandable rows, not the fixed ones at the top)
  const table6Rows = useMemo(() => {
    const rows = [
      { label: 'Managed by NOC (All)', getValue: (index: number) => data?.resolutionBuckets[index]?.managedByNOC || 0, getTotal: () => data?.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) || 0 },
      { label: 'Started at NOC', getValue: (index: number) => view6Mode === 'count' ? (data?.nocTransfers.timeSeries[index]?.startedAtNOC || 0) : ((data?.nocTransfers.timeSeries[index]?.startedAtNOC || 0) / (data?.resolutionBuckets[index]?.managedByNOC || 1)) * 100, getTotal: () => view6Mode === 'count' ? (data?.nocTransfers.total || 0) : ((data?.nocTransfers.total || 0) / (data?.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) || 1)) * 100 },
      { label: 'Solved by automation', getValue: (index: number) => view6Mode === 'count' ? (data?.nocTransfers.timeSeries[index]?.solvedByAutomation || 0) : ((data?.nocTransfers.timeSeries[index]?.solvedByAutomation || 0) / (data?.resolutionBuckets[index]?.managedByNOC || 1)) * 100, getTotal: () => view6Mode === 'count' ? (data?.nocTransfers.totalSolvedByAutomation || 0) : ((data?.nocTransfers.totalSolvedByAutomation || 0) / (data?.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) || 1)) * 100 },
      { label: 'Sent Out', getValue: (index: number) => view6Mode === 'count' ? (data?.nocTransfers.timeSeries[index]?.sentOut || 0) : ((data?.nocTransfers.timeSeries[index]?.sentOut || 0) / (data?.resolutionBuckets[index]?.managedByNOC || 1)) * 100, getTotal: () => view6Mode === 'count' ? (data?.nocTransfers.totalSentOut || 0) : ((data?.nocTransfers.totalSentOut || 0) / (data?.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) || 1)) * 100 }
    ];
    
    if (!table6SortColumn || !data) return rows;
    
    const rowsWithValues = rows.map(row => {
      let value = 0;
      if (table6SortColumn === 'total') {
        value = row.getTotal();
      } else {
        const periodIndex = data.nocTransfers.timeSeries.findIndex(t => t.period === table6SortColumn);
        if (periodIndex >= 0) {
          value = row.getValue(periodIndex);
        }
      }
      return { ...row, value };
    });
    
    rowsWithValues.sort((a, b) => {
      return table6SortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    });
    
    return rowsWithValues;
  }, [data, table6SortColumn, table6SortDirection, view6Mode]);

  // Memoized Autocomplete onChange handlers to avoid re-creating closures on every render
  const createSelectAllHandler = useCallback(
    (allOptions: string[], setSelected: React.Dispatch<React.SetStateAction<string[]>>) =>
      (_e: any, newValue: string[]) => {
        const hasSelectAll = newValue.includes(SELECT_ALL_OPTION);
        setSelected(prev => {
          const prevHasSelectAll = prev.length === allOptions.length;
          if (hasSelectAll && !prevHasSelectAll) {
            return allOptions;
          } else if (!hasSelectAll && prevHasSelectAll) {
            return [];
          } else {
            return newValue.filter(v => v !== SELECT_ALL_OPTION);
          }
        });
        setFiltersDirty(true);
      },
    []
  );

  const handleCIClassChange = useMemo(() => createSelectAllHandler(sortedCIClassFilters, setSelectedCIClasses), [sortedCIClassFilters, createSelectAllHandler]);
  const handleCloseCodeChange = useMemo(() => createSelectAllHandler(sortedCloseCodeFilters, setSelectedCloseCodes), [sortedCloseCodeFilters, createSelectAllHandler]);
  const handleOpenedByChange = useMemo(() => createSelectAllHandler(sortedOpenedByFilters, setSelectedOpenedBy), [sortedOpenedByFilters, createSelectAllHandler]);
  const handleAssignmentGroupChange = useMemo(() => createSelectAllHandler(sortedAssignmentGroupFilters, setSelectedAssignmentGroups), [sortedAssignmentGroupFilters, createSelectAllHandler]);
  const handleLocationFilterChange = useMemo(() => createSelectAllHandler(sortedLocationFilters, setSelectedLocations), [sortedLocationFilters, createSelectAllHandler]);
  const handleStateFilterChange = useMemo(() => createSelectAllHandler(sortedStateFilters, setSelectedStates), [sortedStateFilters, createSelectAllHandler]);

  // Show loading spinner on initial load (when we have no data yet)
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DashboardIcon sx={{ fontSize: 32, color: '#0067b3' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#001a35' }}>
            NOC Dashboard
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={loading || rawDataLoading}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ color: '#0067b3' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
          </Box>
          {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
        </Box>

        <Collapse in={filtersExpanded}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Grouping and Group By */}
              <Grid item xs={12} md={3}>
                <ToggleButtonGroup
                  value={filters.grouping}
                  exclusive
                  onChange={(e, val) => {
                    if (val) {
                      setFilters({ ...filters, grouping: val });
                      setAppliedFilters({ ...appliedFilters, grouping: val });
                    }
                  }}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="weekly">Weekly</ToggleButton>
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Group By</InputLabel>
                  <Select
                    value={filters.group_by}
                    label="Group By"
                    onChange={(e) => {
                      const newGroupBy = e.target.value as 'resolved_at' | 'opened_at';
                      setFilters({ ...filters, group_by: newGroupBy });
                      setAppliedFilters({ ...appliedFilters, group_by: newGroupBy });
                    }}
                  >
                    <MenuItem value="resolved_at">Resolved Date</MenuItem>
                    <MenuItem value="opened_at">Opened Date</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

            
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Opened Date Period Filter */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#001a35', fontSize: '0.875rem' }}>
              Opened Date Period
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  fullWidth
                  onClick={(e) => setOpenedPeriodAnchorEl(e.currentTarget)}
                  endIcon={<KeyboardArrowDown />}
                  sx={{ height: '40px', textTransform: 'none', justifyContent: 'space-between', fontSize: '0.813rem', borderColor: 'rgba(0, 0, 0, 0.23)', '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)', backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  {openedYear ? `${openedYear}${openedMonth ? ` / ${allMonthOptions.find(m => m.value === openedMonth)?.label}` : ''}${openedWeek ? ` / Wk ${openedWeek}` : ''}` : 'Select Period'}
                </Button>
                <Popover
                  open={Boolean(openedPeriodAnchorEl)}
                  anchorEl={openedPeriodAnchorEl}
                  onClose={() => setOpenedPeriodAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2, minWidth: 220 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Year</InputLabel>
                      <Select value={openedYear} label="Year" onChange={(e) => handleOpenedYearChange(e.target.value)} sx={{ fontSize: '0.875rem' }}>
                        <MenuItem value="">All</MenuItem>
                        {yearOptions.map((year) => (
                          <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {openedYear && (
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Month</InputLabel>
                        <Select value={openedMonth} label="Month" onChange={(e) => handleOpenedMonthChange(e.target.value)} sx={{ fontSize: '0.875rem' }}>
                          <MenuItem value="">All</MenuItem>
                          {getOpenedMonthOptions.map((month) => (
                            <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {openedYear && (
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Week</InputLabel>
                        <Select value={openedWeek} label="Week" onChange={(e) => handleOpenedWeekChange(e.target.value)} sx={{ fontSize: '0.875rem' }}>
                          <MenuItem value="">All</MenuItem>
                          {getOpenedWeekOptions.map((week) => (
                            <MenuItem key={week} value={week.toString()}>Week {week}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Popover>
              </Grid>
              <Chip label="OR" size="small" variant="outlined" sx={{ color: '#001a35', fontSize: '0.75rem', mx: 1 }} />
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Preset Period</InputLabel>
                  <Select
                    value={openedPeriodPreset}
                    label="Preset Period"
                    onChange={(e) => handleOpenedPeriodPresetChange(e.target.value as any)}
                    sx={{ fontSize: '0.875rem', height: '40px' }}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="last8weeks">Last 8 weeks</MenuItem>
                    <MenuItem value="last4weeks">Last 4 weeks</MenuItem>
                    <MenuItem value="last2weeks">Last 2 weeks</MenuItem>
                    <MenuItem value="last7days">Last 7 days</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {openedPeriodPreset === 'custom' && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      type="date"
                      size="small"
                      label="Start Date"
                      fullWidth
                      value={openedStartDateInput}
                      onChange={(e) => handleOpenedStartDateChange(e.target.value)}
                      InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }}
                      inputProps={{
                        style: { fontSize: '0.875rem' },
                        min: slicerOptions?.dateRange?.opened_at?.min?.split('T')[0],
                        max: slicerOptions?.dateRange?.opened_at?.max?.split('T')[0]
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      type="date"
                      size="small"
                      label="End Date"
                      fullWidth
                      value={openedEndDateInput}
                      onChange={(e) => handleOpenedEndDateChange(e.target.value)}
                      InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }}
                      inputProps={{
                        style: { fontSize: '0.875rem' },
                        min: slicerOptions?.dateRange?.opened_at?.min?.split('T')[0],
                        max: slicerOptions?.dateRange?.opened_at?.max?.split('T')[0]
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={clearOpenedDates}
                  startIcon={<Clear />}
                  sx={{ height: '40px', fontSize: '0.75rem' }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>

            {/* Resolved Date Period Filter */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#001a35', fontSize: '0.875rem' }}>
              Resolved Date Period
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  fullWidth
                  onClick={(e) => setResolvedPeriodAnchorEl(e.currentTarget)}
                  endIcon={<KeyboardArrowDown />}
                  sx={{ height: '40px', textTransform: 'none', justifyContent: 'space-between', fontSize: '0.813rem', borderColor: 'rgba(0, 0, 0, 0.23)', '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)', backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  {resolvedYear ? `${resolvedYear}${resolvedMonth ? ` / ${allMonthOptions.find(m => m.value === resolvedMonth)?.label}` : ''}${resolvedWeek ? ` / Wk ${resolvedWeek}` : ''}` : 'Select Period'}
                </Button>
                <Popover
                  open={Boolean(resolvedPeriodAnchorEl)}
                  anchorEl={resolvedPeriodAnchorEl}
                  onClose={() => setResolvedPeriodAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2, minWidth: 220 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Year</InputLabel>
                      <Select value={resolvedYear} label="Year" onChange={(e) => handleResolvedYearChange(e.target.value)} sx={{ fontSize: '0.875rem' }}>
                        <MenuItem value="">All</MenuItem>
                        {yearOptions.map((year) => (
                          <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {resolvedYear && (
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Month</InputLabel>
                        <Select value={resolvedMonth} label="Month" onChange={(e) => handleResolvedMonthChange(e.target.value)} sx={{ fontSize: '0.875rem' }}>
                          <MenuItem value="">All</MenuItem>
                          {getResolvedMonthOptions.map((month) => (
                            <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {resolvedYear && (
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Week</InputLabel>
                        <Select value={resolvedWeek} label="Week" onChange={(e) => handleResolvedWeekChange(e.target.value)} sx={{ fontSize: '0.875rem' }}>
                          <MenuItem value="">All</MenuItem>
                          {getResolvedWeekOptions.map((week) => (
                            <MenuItem key={week} value={week.toString()}>Week {week}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Popover>
              </Grid>
              <Chip label="OR" size="small" variant="outlined" sx={{ color: '#001a35', fontSize: '0.75rem', mx: 1 }} />
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Preset Period</InputLabel>
                  <Select
                    value={resolvedPeriodPreset}
                    label="Preset Period"
                    onChange={(e) => handleResolvedPeriodPresetChange(e.target.value as any)}
                    sx={{ fontSize: '0.875rem', height: '40px' }}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="last8weeks">Last 8 weeks</MenuItem>
                    <MenuItem value="last4weeks">Last 4 weeks</MenuItem>
                    <MenuItem value="last2weeks">Last 2 weeks</MenuItem>
                    <MenuItem value="last7days">Last 7 days</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {resolvedPeriodPreset === 'custom' && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      type="date"
                      size="small"
                      label="Start Date"
                      fullWidth
                      value={resolvedStartDateInput}
                      onChange={(e) => handleResolvedStartDateChange(e.target.value)}
                      InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }}
                      inputProps={{
                        style: { fontSize: '0.875rem' },
                        min: slicerOptions?.dateRange?.resolved_at?.min?.split('T')[0],
                        max: slicerOptions?.dateRange?.resolved_at?.max?.split('T')[0]
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      type="date"
                      size="small"
                      label="End Date"
                      fullWidth
                      value={resolvedEndDateInput}
                      onChange={(e) => handleResolvedEndDateChange(e.target.value)}
                      InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }}
                      inputProps={{
                        style: { fontSize: '0.875rem' },
                        min: slicerOptions?.dateRange?.resolved_at?.min?.split('T')[0],
                        max: slicerOptions?.dateRange?.resolved_at?.max?.split('T')[0]
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={clearResolvedDates}
                  startIcon={<Clear />}
                  sx={{ height: '40px', fontSize: '0.75rem' }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Multi-select Filters */}
            <Grid container spacing={1}>
              {slicerOptions && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      limitTags={1}
                      options={[SELECT_ALL_OPTION, ...sortedCIClassFilters]}
                      value={selectedCIClasses.length === sortedCIClassFilters.length && sortedCIClassFilters.length > 0 ? [SELECT_ALL_OPTION, ...selectedCIClasses] : selectedCIClasses}
                      onChange={handleCIClassChange}
                      renderInput={(params) => <TextField {...params} label="CI Class" sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', cursor: 'pointer', minHeight: '40px', maxHeight: '40px' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)' } }} />}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...otherProps } = props;
                        const isSelectAll = option === SELECT_ALL_OPTION;
                        const isAllSelected = selectedCIClasses.length === sortedCIClassFilters.length && sortedCIClassFilters.length > 0;
                        return (
                          <li key={key} {...otherProps} style={{ ...otherProps.style, cursor: 'pointer', fontWeight: isSelectAll ? 600 : 'normal' }}>
                            <Checkbox checked={isSelectAll ? isAllSelected : selected} size="small" />
                            <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                          </li>
                        );
                      }}
                      sx={{ '& .MuiChip-root': { fontSize: '0.75rem', height: '24px' }, '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' }, cursor: 'pointer' }}
                      renderTags={(value, getTagProps) => {
                        const isAllSelected = selectedCIClasses.length === sortedCIClassFilters.length && sortedCIClassFilters.length > 0;
                        const filteredValue = value.filter(v => v !== SELECT_ALL_OPTION);
                        
                        if (isAllSelected) {
                          const { key, ...chipProps } = getTagProps({ index: 0 });
                          return (
                            <Chip
                              key={key}
                              {...chipProps}
                              label={SELECT_ALL_OPTION}
                              size="small"
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          );
                        }
                        
                        if (filteredValue.length === 0) return null;
                        
                        const displayValue = filteredValue[0];
                        const remainingCount = filteredValue.length - 1;
                        const { key, ...chipProps } = getTagProps({ index: 0 });
                        
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={remainingCount > 0 ? `${displayValue} +${remainingCount}` : displayValue}
                            size="small"
                            sx={{ fontSize: '0.75rem', height: '24px', maxWidth: '100%' }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      limitTags={1}
                      options={[SELECT_ALL_OPTION, ...sortedCloseCodeFilters]}
                      value={selectedCloseCodes.length === sortedCloseCodeFilters.length && sortedCloseCodeFilters.length > 0 ? [SELECT_ALL_OPTION, ...selectedCloseCodes] : selectedCloseCodes}
                      onChange={handleCloseCodeChange}
                      renderInput={(params) => <TextField {...params} label="Close Code" sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', cursor: 'pointer', minHeight: '40px', maxHeight: '40px' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)' } }} />}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...otherProps } = props;
                        const isSelectAll = option === SELECT_ALL_OPTION;
                        const isAllSelected = selectedCloseCodes.length === sortedCloseCodeFilters.length && sortedCloseCodeFilters.length > 0;
                        return (
                          <li key={key} {...otherProps} style={{ ...otherProps.style, cursor: 'pointer', fontWeight: isSelectAll ? 600 : 'normal' }}>
                            <Checkbox checked={isSelectAll ? isAllSelected : selected} size="small" />
                            <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                          </li>
                        );
                      }}
                      sx={{ '& .MuiChip-root': { fontSize: '0.75rem', height: '24px' }, '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' }, cursor: 'pointer' }}
                      renderTags={(value, getTagProps) => {
                        const isAllSelected = selectedCloseCodes.length === sortedCloseCodeFilters.length && sortedCloseCodeFilters.length > 0;
                        const filteredValue = value.filter(v => v !== SELECT_ALL_OPTION);
                        
                        if (isAllSelected) {
                          const { key, ...chipProps } = getTagProps({ index: 0 });
                          return (
                            <Chip
                              key={key}
                              {...chipProps}
                              label={SELECT_ALL_OPTION}
                              size="small"
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          );
                        }
                        
                        if (filteredValue.length === 0) return null;
                        
                        const displayValue = filteredValue[0];
                        const remainingCount = filteredValue.length - 1;
                        const { key, ...chipProps } = getTagProps({ index: 0 });
                        
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={remainingCount > 0 ? `${displayValue} +${remainingCount}` : displayValue}
                            size="small"
                            sx={{ fontSize: '0.75rem', height: '24px', maxWidth: '100%' }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      limitTags={1}
                      options={[SELECT_ALL_OPTION, ...sortedOpenedByFilters]}
                      value={selectedOpenedBy.length === sortedOpenedByFilters.length && sortedOpenedByFilters.length > 0 ? [SELECT_ALL_OPTION, ...selectedOpenedBy] : selectedOpenedBy}
                      onChange={handleOpenedByChange}
                      disableCloseOnSelect
                      renderInput={(params) => <TextField {...params} label="Opened By" placeholder="Search..." sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', cursor: 'pointer', minHeight: '40px', maxHeight: '40px' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)' } }} />}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...otherProps } = props;
                        const isSelectAll = option === SELECT_ALL_OPTION;
                        const isAllSelected = selectedOpenedBy.length === sortedOpenedByFilters.length && sortedOpenedByFilters.length > 0;
                        return (
                          <li key={key} {...otherProps} style={{ ...otherProps.style, cursor: 'pointer', fontWeight: isSelectAll ? 600 : 'normal' }}>
                            <Checkbox checked={isSelectAll ? isAllSelected : selected} size="small" />
                            <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                          </li>
                        );
                      }}
                      sx={{ '& .MuiChip-root': { fontSize: '0.75rem', height: '24px' }, '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' }, cursor: 'pointer' }}
                      renderTags={(value, getTagProps) => {
                        const isAllSelected = selectedOpenedBy.length === sortedOpenedByFilters.length && sortedOpenedByFilters.length > 0;
                        const filteredValue = value.filter(v => v !== SELECT_ALL_OPTION);
                        
                        if (isAllSelected) {
                          const { key, ...chipProps } = getTagProps({ index: 0 });
                          return (
                            <Chip
                              key={key}
                              {...chipProps}
                              label={SELECT_ALL_OPTION}
                              size="small"
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          );
                        }
                        
                        if (filteredValue.length === 0) return null;
                        
                        const displayValue = filteredValue[0];
                        const remainingCount = filteredValue.length - 1;
                        const { key, ...chipProps } = getTagProps({ index: 0 });
                        
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={remainingCount > 0 ? `${displayValue} +${remainingCount}` : displayValue}
                            size="small"
                            sx={{ fontSize: '0.75rem', height: '24px', maxWidth: '100%' }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      limitTags={1}
                      options={[SELECT_ALL_OPTION, ...sortedAssignmentGroupFilters]}
                      value={selectedAssignmentGroups.length === sortedAssignmentGroupFilters.length && sortedAssignmentGroupFilters.length > 0 ? [SELECT_ALL_OPTION, ...selectedAssignmentGroups] : selectedAssignmentGroups}
                      onChange={handleAssignmentGroupChange}
                      renderInput={(params) => <TextField {...params} label="Assignment Group" sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', cursor: 'pointer', minHeight: '40px', maxHeight: '40px' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)' } }} />}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...otherProps } = props;
                        const isSelectAll = option === SELECT_ALL_OPTION;
                        const isAllSelected = selectedAssignmentGroups.length === sortedAssignmentGroupFilters.length && sortedAssignmentGroupFilters.length > 0;
                        return (
                          <li key={key} {...otherProps} style={{ ...otherProps.style, cursor: 'pointer', fontWeight: isSelectAll ? 600 : 'normal' }}>
                            <Checkbox checked={isSelectAll ? isAllSelected : selected} size="small" />
                            <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                          </li>
                        );
                      }}
                      sx={{ '& .MuiChip-root': { fontSize: '0.75rem', height: '24px' }, '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' }, cursor: 'pointer' }}
                      renderTags={(value, getTagProps) => {
                        const isAllSelected = selectedAssignmentGroups.length === sortedAssignmentGroupFilters.length && sortedAssignmentGroupFilters.length > 0;
                        const filteredValue = value.filter(v => v !== SELECT_ALL_OPTION);
                        
                        if (isAllSelected) {
                          const { key, ...chipProps } = getTagProps({ index: 0 });
                          return (
                            <Chip
                              key={key}
                              {...chipProps}
                              label={SELECT_ALL_OPTION}
                              size="small"
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          );
                        }
                        
                        if (filteredValue.length === 0) return null;
                        
                        const displayValue = filteredValue[0];
                        const remainingCount = filteredValue.length - 1;
                        const { key, ...chipProps } = getTagProps({ index: 0 });
                        
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={remainingCount > 0 ? `${displayValue} +${remainingCount}` : displayValue}
                            size="small"
                            sx={{ fontSize: '0.75rem', height: '24px', maxWidth: '100%' }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      limitTags={1}
                      options={[SELECT_ALL_OPTION, ...sortedLocationFilters]}
                      value={selectedLocations.length === sortedLocationFilters.length && sortedLocationFilters.length > 0 ? [SELECT_ALL_OPTION, ...selectedLocations] : selectedLocations}
                      onChange={handleLocationFilterChange}
                      renderInput={(params) => <TextField {...params} label="Location" sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', cursor: 'pointer', minHeight: '40px', maxHeight: '40px' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)' } }} />}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...otherProps } = props;
                        const isSelectAll = option === SELECT_ALL_OPTION;
                        const isAllSelected = selectedLocations.length === sortedLocationFilters.length && sortedLocationFilters.length > 0;
                        return (
                          <li key={key} {...otherProps} style={{ ...otherProps.style, cursor: 'pointer', fontWeight: isSelectAll ? 600 : 'normal' }}>
                            <Checkbox checked={isSelectAll ? isAllSelected : selected} size="small" />
                            <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                          </li>
                        );
                      }}
                      sx={{ '& .MuiChip-root': { fontSize: '0.75rem', height: '24px' }, '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' }, cursor: 'pointer' }}
                      renderTags={(value, getTagProps) => {
                        const isAllSelected = selectedLocations.length === sortedLocationFilters.length && sortedLocationFilters.length > 0;
                        const filteredValue = value.filter(v => v !== SELECT_ALL_OPTION);
                        
                        if (isAllSelected) {
                          const { key, ...chipProps } = getTagProps({ index: 0 });
                          return (
                            <Chip
                              key={key}
                              {...chipProps}
                              label={SELECT_ALL_OPTION}
                              size="small"
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          );
                        }
                        
                        if (filteredValue.length === 0) return null;
                        
                        const displayValue = filteredValue[0];
                        const remainingCount = filteredValue.length - 1;
                        const { key, ...chipProps } = getTagProps({ index: 0 });
                        
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={remainingCount > 0 ? `${displayValue} +${remainingCount}` : displayValue}
                            size="small"
                            sx={{ fontSize: '0.75rem', height: '24px', maxWidth: '100%' }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl 
                      size="small" 
                      fullWidth
                      sx={{ 
                        '& .MuiInputBase-root': { 
                          fontSize: '0.875rem', 
                          cursor: 'pointer', 
                          minHeight: '40px', 
                          maxHeight: '40px' 
                        }, 
                        '& .MuiInputLabel-root': { 
                          fontSize: '0.875rem' 
                        } 
                      }}
                    >
                      <InputLabel>Vendor Ticket</InputLabel>
                      <Select<'' | 'yes' | 'no'>
                        value={vendorTicket}
                        label="Vendor Ticket"
                        onChange={(e) => { setVendorTicket(e.target.value as '' | 'yes' | 'no'); setFiltersDirty(true); }}
                      >
                        <MenuItem value="">
                          <em style={{ fontStyle: 'normal', color: '#9e9e9e' }}>None</em>
                        </MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      limitTags={1}
                      options={[SELECT_ALL_OPTION, ...sortedStateFilters]}
                      value={selectedStates.length === sortedStateFilters.length && sortedStateFilters.length > 0 ? [SELECT_ALL_OPTION, ...selectedStates] : selectedStates}
                      onChange={handleStateFilterChange}
                      renderInput={(params) => <TextField {...params} label="State" sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', cursor: 'pointer', minHeight: '40px', maxHeight: '40px' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)' } }} />}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...otherProps } = props;
                        const isSelectAll = option === SELECT_ALL_OPTION;
                        const isAllSelected = selectedStates.length === sortedStateFilters.length && sortedStateFilters.length > 0;
                        return (
                          <li key={key} {...otherProps} style={{ ...otherProps.style, cursor: 'pointer', fontWeight: isSelectAll ? 600 : 'normal' }}>
                            <Checkbox checked={isSelectAll ? isAllSelected : selected} size="small" />
                            <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                          </li>
                        );
                      }}
                      sx={{ '& .MuiChip-root': { fontSize: '0.75rem', height: '24px' }, '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' }, cursor: 'pointer' }}
                      renderTags={(value, getTagProps) => {
                        const isAllSelected = selectedStates.length === sortedStateFilters.length && sortedStateFilters.length > 0;
                        const filteredValue = value.filter(v => v !== SELECT_ALL_OPTION);
                        
                        if (isAllSelected) {
                          const { key, ...chipProps } = getTagProps({ index: 0 });
                          return (
                            <Chip
                              key={key}
                              {...chipProps}
                              label={SELECT_ALL_OPTION}
                              size="small"
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          );
                        }
                        
                        if (filteredValue.length === 0) return null;
                        
                        const displayValue = filteredValue[0];
                        const remainingCount = filteredValue.length - 1;
                        const { key, ...chipProps } = getTagProps({ index: 0 });
                        
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={remainingCount > 0 ? `${displayValue} +${remainingCount}` : displayValue}
                            size="small"
                            sx={{ fontSize: '0.75rem', height: '24px', maxWidth: '100%' }}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl 
                      size="small" 
                      fullWidth
                      sx={{ 
                        '& .MuiInputBase-root': { 
                          fontSize: '0.875rem', 
                          cursor: 'pointer', 
                          minHeight: '40px', 
                          maxHeight: '40px' 
                        }, 
                        '& .MuiInputLabel-root': { 
                          fontSize: '0.875rem' 
                        } 
                      }}
                    >
                      <InputLabel>Resolved By</InputLabel>
                      <Select<'' | 'NOC' | 'Others'>
                        value={resolvedBy}
                        label="Resolved By"
                        onChange={(e) => { setResolvedBy(e.target.value as '' | 'NOC' | 'Others'); setFiltersDirty(true); }}
                      >
                        <MenuItem value="">
                          <em style={{ fontStyle: 'normal', color: '#9e9e9e' }}>All</em>
                        </MenuItem>
                        <MenuItem value="NOC">NOC</MenuItem>
                        <MenuItem value="Others">Others</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          minHeight: '40px',
                          maxHeight: '40px'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem'
                        }
                      }}
                    >
                      <InputLabel>Detected by Alert</InputLabel>
                      <Select<YesNoAll>
                        value={detectedByAlert}
                        label="Detected by Alert"
                        onChange={(e) => { setDetectedByAlert(e.target.value as YesNoAll); setFiltersDirty(true); }}
                      >
                        <MenuItem value="">
                          <em style={{ fontStyle: 'normal', color: '#9e9e9e' }}>All</em>
                        </MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          minHeight: '40px',
                          maxHeight: '40px'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem'
                        }
                      }}
                    >
                      <InputLabel>Has Parent Incident</InputLabel>
                      <Select<YesNoAll>
                        value={parentIncident}
                        label="Has Parent Incident"
                        onChange={(e) => { setParentIncident(e.target.value as YesNoAll); setFiltersDirty(true); }}
                      >
                        <MenuItem value="">
                          <em style={{ fontStyle: 'normal', color: '#9e9e9e' }}>All</em>
                        </MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button 
                variant={filtersDirty ? 'contained' : 'outlined'}
                onClick={handleApplyFilters}
                disabled={!filtersDirty}
                sx={{
                  textTransform: 'none',
                  ...(filtersDirty && {
                    backgroundColor: '#0067b3',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#005a9e',
                    },
                  }),
                  ...(!filtersDirty && {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                    borderColor: '#e0e0e0',
                  }),
                }}
              >
                Apply Filters
              </Button>
              <Button variant="outlined" onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Sub Tabs: Tabular / Visual / Raw Data */}
      {data && (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
              },
            }}
          >
            <Tab label="Tabular" value="tabular" />
            <Tab label="Visual" value="visual" />
            <Tab label="Raw Data" value="rawdata" />
          </Tabs>
        </Paper>
      )}

      {/* Container for both views */}
      <Box sx={{ position: 'relative' }}>

      {/* Tabular View - always mounted */}
      <Box
        sx={{
          ...(activeTab !== 'tabular' && {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            visibility: 'hidden',
            pointerEvents: 'none',
          }),
        }}
      >
      {data && (
        <>
      {/* Total Incidents Card */}
      {data && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card sx={{ border: '2px solid #0067b3', backgroundColor: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" >
                      Total Incidents
                    </Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Typography variant="h3" sx={{ color: '#0067b3', fontWeight: 700 }}>
                    {data.metadata.totalRecords.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* All Visuals in 2-Column Grid */}
          <Grid container spacing={3}>
            {/* Visual 1: Incident Counts by Category */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents by Category
                    </Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportIncidentsByCategory}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view1Mode}
                      exclusive
                      onChange={(e, val) => val && setView1Mode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 150, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          Category
                        </TableCell>
                        {data.resolutionBuckets.map((bucket) => (
                          <SortableHeader
                            key={bucket.period}
                            column={bucket.period}
                            label={bucket.period}
                            currentSortColumn={table1SortColumn}
                            currentSortDirection={table1SortDirection}
                            onSort={handleTable1Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table1SortColumn}
                          currentSortDirection={table1SortDirection}
                          onSort={handleTable1Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {table1Rows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>
                            {row.label}
                          </TableCell>
                          {data.resolutionBuckets.map((bucket) => (
                            <TableCell key={bucket.period} align="center">
                              {row.label === 'Managed by NOC (All)' 
                                ? bucket.managedByNOC
                                : row.label === 'Resolved by NOC'
                                  ? view1Mode === 'count' ? bucket.resolvedByNOC : bucket.managedByNOC > 0 ? ((bucket.resolvedByNOC / bucket.managedByNOC) * 100).toFixed(1) + '%' : '0.0%'
                                  : row.label === 'Resolved by Other'
                                    ? view1Mode === 'count' ? bucket.resolvedByOther : bucket.managedByNOC > 0 ? ((bucket.resolvedByOther / bucket.managedByNOC) * 100).toFixed(1) + '%' : '0.0%'
                                    : view1Mode === 'count' ? bucket.cancelled : bucket.managedByNOC > 0 ? ((bucket.cancelled / bucket.managedByNOC) * 100).toFixed(1) + '%' : '0.0%'
                              }
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, zIndex: 1 }}>
                            {row.label === 'Managed by NOC (All)'
                              ? data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)
                              : row.label === 'Resolved by NOC'
                                ? view1Mode === 'count' 
                                  ? data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByNOC, 0) 
                                  : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 
                                    ? ((data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByNOC, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100).toFixed(1) + '%' 
                                    : '0.0%'
                                : row.label === 'Resolved by Other'
                                  ? view1Mode === 'count' 
                                    ? data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByOther, 0) 
                                    : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 
                                      ? ((data.resolutionBuckets.reduce((sum, b) => sum + b.resolvedByOther, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100).toFixed(1) + '%' 
                                      : '0.0%'
                                  : view1Mode === 'count' 
                                    ? data.resolutionBuckets.reduce((sum, b) => sum + b.cancelled, 0) 
                                    : data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0) > 0 
                                      ? ((data.resolutionBuckets.reduce((sum, b) => sum + b.cancelled, 0) / data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)) * 100).toFixed(1) + '%' 
                                      : '0.0%'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Visual 2: MTTR by Category */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      MTTR by Category (hours)
                    </Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Tooltip title="Export to CSV">
                    <Button
                      size="small"
                      onClick={exportMTTRByCategory}
                      startIcon={<FileDownload />}
                      sx={{ minWidth: 'auto', textTransform: 'none' }}
                    >
                      CSV
                    </Button>
                  </Tooltip>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 150, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          Category
                        </TableCell>
                        {data.resolutionBuckets.map((bucket) => (
                          <SortableHeader
                            key={bucket.period}
                            column={bucket.period}
                            label={bucket.period}
                            currentSortColumn={table2SortColumn}
                            currentSortDirection={table2SortDirection}
                            onSort={handleTable2Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Average"
                          currentSortColumn={table2SortColumn}
                          currentSortDirection={table2SortDirection}
                          onSort={handleTable2Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {table2Rows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>
                            {row.label}
                          </TableCell>
                          {data.resolutionBuckets.map((bucket) => (
                            <TableCell key={bucket.period} align="center">
                              {row.getValue(bucket) != null ? row.getValue(bucket).toFixed(2) : 'N/A'}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, zIndex: 1 }}>
                            {(data.resolutionBuckets.reduce((sum, b) => sum + row.getValue(b), 0) / data.resolutionBuckets.length).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

             {/* Visual 3: Resolution Code Split */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents by Resolution Code
                    </Typography>
                    <Chip label={`${sortedResolutionCodes.length} total`} size="small" />
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportResolutionCodeSplit}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view3Mode}
                      exclusive
                      onChange={(e, val) => val && setView3Mode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ position: 'relative' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 200, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          Resolution Code
                        </TableCell>
                        {data.resolutionCodeSplit.map((split) => (
                          <SortableHeader
                            key={split.period}
                            column={split.period}
                            label={split.period}
                            currentSortColumn={table3SortColumn}
                            currentSortDirection={table3SortDirection}
                            onSort={handleTable3Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table3SortColumn}
                          currentSortDirection={table3SortDirection}
                          onSort={handleTable3Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedResolutionCodes.map((code) => {
                        const rowTotal = resolutionCodeTotals.rowTotals[code] || 0;
                        const rowPercentage = resolutionCodeTotals.grandTotal > 0 ? (rowTotal / resolutionCodeTotals.grandTotal) * 100 : 0;
                        return (
                          <TableRow key={code}>
                            <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, fontSize: '0.75rem' }}>
                              {code}
                            </TableCell>
                            {data.resolutionCodeSplit.map((split) => {
                              const count = split.codes[code] || 0;
                              const periodTotal = resolutionCodeTotals.periodTotals[split.period] || 0;
                              const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
                              return (
                                <TableCell key={split.period} align="center" sx={{ fontSize: '0.75rem' }}>
                                  {view3Mode === 'count' ? count : `${percentage.toFixed(1)}%`}
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 1 }}>
                              {view3Mode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total Row - sticky at bottom */}
                      <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f7fa', borderTop: '2px solid #ddd', zIndex: 2 }}>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: '#f5f7fa', zIndex: 3, fontSize: '0.75rem' }}>
                          Total
                        </TableCell>
                        {data.resolutionCodeSplit.map((split) => {
                          const periodTotal = resolutionCodeTotals.periodTotals[split.period] || 0;
                          return (
                            <TableCell key={split.period} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                              {view3Mode === 'count' ? periodTotal : '100.0%'}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#e3f2fd', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 3 }}>
                          {view3Mode === 'count' ? resolutionCodeTotals.grandTotal : '100.0%'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Visual 4: MTTR Buckets */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents by MTTR Buckets
                    </Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportMTTRBuckets}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view4Mode}
                      exclusive
                      onChange={(e, val) => val && setView4Mode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 120, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          MTTR Bucket
                        </TableCell>
                        {data.mttrBuckets.map((bucket) => (
                          <SortableHeader
                            key={bucket.period}
                            column={bucket.period}
                            label={bucket.period}
                            currentSortColumn={table4SortColumn}
                            currentSortDirection={table4SortDirection}
                            onSort={handleTable4Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table4SortColumn}
                          currentSortDirection={table4SortDirection}
                          onSort={handleTable4Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedMTTRBuckets.map((bucketName) => {
                        const rowTotal = data.mttrBuckets.reduce((sum, b) => sum + b.buckets[bucketName as keyof typeof b.buckets], 0);
                        const grandTotal = data.mttrBuckets.reduce((sum, b) => sum + Object.values(b.buckets).reduce((s, val) => s + val, 0), 0);
                        const rowPercentage = grandTotal > 0 ? (rowTotal / grandTotal) * 100 : 0;
                        return (
                          <TableRow key={bucketName}>
                            <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>
                              {bucketName} 
                            </TableCell>
                            {data.mttrBuckets.map((bucket) => {
                              const count = bucket.buckets[bucketName as keyof typeof bucket.buckets];
                              const total = Object.values(bucket.buckets).reduce((sum, val) => sum + val, 0);
                              const percentage = total > 0 ? (count / total) * 100 : 0;
                              return (
                                <TableCell key={bucket.period} align="center">
                                  {view4Mode === 'count' ? count : `${percentage.toFixed(1)}%`}
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, zIndex: 1 }}>
                              {view4Mode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Visual 5: State and Hold Reasons */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents by State
                    </Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportStateAndHoldReasons}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view5Mode}
                      exclusive
                      onChange={(e, val) => val && setView5Mode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ position: 'relative' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 150, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          State
                        </TableCell>
                        {data.stateAndHoldReasons.map((item) => (
                          <SortableHeader
                            key={item.period}
                            column={item.period}
                            label={item.period}
                            currentSortColumn={table5SortColumn}
                            currentSortDirection={table5SortDirection}
                            onSort={handleTable5Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table5SortColumn}
                          currentSortDirection={table5SortDirection}
                          onSort={handleTable5Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedStates.map((state) => {
                        const rowTotal = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons[state]?.count || 0), 0);
                        const grandTotal = data.stateAndHoldReasons.reduce((sum, item) => sum + Object.values(item.stateReasons).reduce((s, sr) => s + sr.count, 0), 0);
                        const rowPercentage = grandTotal > 0 ? (rowTotal / grandTotal) * 100 : 0;
                        return (
                          <React.Fragment key={state}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, cursor: state === 'On Hold' && allHoldReasons.length > 0 ? 'pointer' : 'default' }} onClick={() => state === 'On Hold' && allHoldReasons.length > 0 && setShowHoldReasons(!showHoldReasons)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {state}
                                  {state === 'On Hold' && allHoldReasons.length > 0 && (showHoldReasons ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />)}
                                </Box>
                              </TableCell>
                              {data.stateAndHoldReasons.map((item) => {
                                const count = item.stateReasons[state]?.count || 0;
                                const total = Object.values(item.stateReasons).reduce((sum, s) => sum + s.count, 0);
                                const percentage = total > 0 ? (count / total) * 100 : 0;
                                return (
                                  <TableCell key={item.period} align="center">
                                    {view5Mode === 'count' ? count : `${percentage.toFixed(1)}%`}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, zIndex: 1 }}>
                                {view5Mode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`}
                              </TableCell>
                            </TableRow>
                            {state === 'On Hold' && showHoldReasons && sortedHoldReasons.map((reason) => (
                              <TableRow key={`${state}-${reason}`} sx={{ backgroundColor: '#f9f9f9' }}>
                                <TableCell sx={{ pl: 4, fontSize: '0.75rem', position: 'sticky', left: 0, backgroundColor: '#f9f9f9', zIndex: 1 }}>
                                  {reason}
                                </TableCell>
                                {data.stateAndHoldReasons.map((item) => {
                                  const count = item.stateReasons['On Hold']?.holdReasons[reason] || 0;
                                  const totalOnHoldForPeriod = item.stateReasons['On Hold']?.count || 0;
                                  const percentage = totalOnHoldForPeriod > 0 ? (count / totalOnHoldForPeriod) * 100 : 0;
                                  return (
                                    <TableCell key={item.period} align="center" sx={{ fontSize: '0.75rem' }}>
                                      {view5Mode === 'count' ? count : `${percentage.toFixed(1)}%`}
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: '#f5f7fa', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 1 }}>
                                  {(() => {
                                    const totalReasonCount = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons['On Hold']?.holdReasons[reason] || 0), 0);
                                    const totalOnHold = data.stateAndHoldReasons.reduce((sum, item) => sum + (item.stateReasons['On Hold']?.count || 0), 0);
                                    const totalPercentage = totalOnHold > 0 ? (totalReasonCount / totalOnHold) * 100 : 0;
                                    return view5Mode === 'count' ? totalReasonCount : `${totalPercentage.toFixed(1)}%`;
                                  })()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {/* Total Row - sticky at bottom */}
                      <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f7fa', borderTop: '2px solid #ddd', zIndex: 2 }}>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: '#f5f7fa', zIndex: 3, fontSize: '0.75rem' }}>
                          Total
                        </TableCell>
                        {data.stateAndHoldReasons.map((item) => {
                          const periodTotal = Object.values(item.stateReasons).reduce((sum, sr) => sum + sr.count, 0);
                          return (
                            <TableCell key={item.period} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                              {view5Mode === 'count' ? periodTotal : '100.0%'}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#e3f2fd', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 3 }}>
                          {view5Mode === 'count' ? data.stateAndHoldReasons.reduce((sum, item) => sum + Object.values(item.stateReasons).reduce((s, sr) => s + sr.count, 0), 0) : '100.0%'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Visual 6: NOC Transfers */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incident Transfers
                    </Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportNOCTransfers}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view6Mode}
                      exclusive
                      onChange={(e, val) => val && setView6Mode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 150, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          Metric
                        </TableCell>
                        {data.nocTransfers.timeSeries.map((transfer) => (
                          <SortableHeader
                            key={transfer.period}
                            column={transfer.period}
                            label={transfer.period}
                            currentSortColumn={table6SortColumn}
                            currentSortDirection={table6SortDirection}
                            onSort={handleTable6Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table6SortColumn}
                          currentSortDirection={table6SortDirection}
                          onSort={handleTable6Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {table6Rows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>
                            {row.label}
                          </TableCell>
                          {data.nocTransfers.timeSeries.map((transfer, index) => (
                            <TableCell key={transfer.period} align="center">
                              {row.label === 'Managed by NOC (All)'
                                ? data.resolutionBuckets[index]?.managedByNOC || 0
                                : view6Mode === 'count'
                                  ? Math.round(row.getValue(index))
                                  : `${row.getValue(index).toFixed(1)}%`
                              }
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, zIndex: 1 }}>
                            {row.label === 'Managed by NOC (All)'
                              ? data.resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0)
                              : view6Mode === 'count'
                                ? Math.round(row.getTotal())
                                : `${row.getTotal().toFixed(1)}%`
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                      {allDestinations.length > 0 && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, cursor: 'pointer' }} onClick={() => setShowDestinations(!showDestinations)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              Destinations
                              {showDestinations ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                            </Box>
                          </TableCell>
                          <TableCell colSpan={data.nocTransfers.timeSeries.length + 1} />
                        </TableRow>
                      )}
                      {showDestinations && (() => {
                        // Create a map from period to destinations data
                        const destMap = new Map<string, any>();
                        data.transferDestinations.timeSeries.forEach((transfer) => {
                          destMap.set(transfer.period, transfer.destinations);
                        });

                        return sortedDestinations.map((dest) => {
                          const destOverall = data.transferDestinations.overallDestinations[dest];
                          const count = destOverall?.count || 0;
                          const percentage = destOverall?.percentage || 0;

                          return (
                            <TableRow key={dest} sx={{ backgroundColor: '#f9f9f9' }}>
                              <TableCell sx={{ pl: 4, fontSize: '0.75rem', position: 'sticky', left: 0, backgroundColor: '#f9f9f9', zIndex: 1 }}>
                                {dest}
                              </TableCell>
                              {data.nocTransfers.timeSeries.map((transfer) => {
                                const periodDests = destMap.get(transfer.period);
                                const destInfo = periodDests?.[dest];
                                const count = destInfo?.count || 0;
                                return (
                                  <TableCell key={transfer.period} align="center" sx={{ fontSize: '0.75rem' }}>
                                    {view6Mode === 'count' ? count : transfer.sentOut > 0 ? ((count / transfer.sentOut) * 100).toFixed(1) + '%' : '0.0%'}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: '#f5f7fa', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 1 }}>
                                {view6Mode === 'count' ? count : data.nocTransfers.totalSentOut > 0 ? ((count / data.nocTransfers.totalSentOut) * 100).toFixed(1) + '%' : '0.0%'}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Visual 7: OBS Incidents */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 250, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents Sent to OBS
                    </Typography>
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Tooltip title="Export to CSV">
                    <Button
                      size="small"
                      onClick={exportOBSIncidents}
                      startIcon={<FileDownload />}
                      sx={{ minWidth: 'auto', textTransform: 'none' }}
                    >
                      CSV
                    </Button>
                  </Tooltip>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 120, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          Metric
                        </TableCell>
                        {data.obsIncidents.timeSeries.map((obs) => (
                          <SortableHeader
                            key={obs.period}
                            column={obs.period}
                            label={obs.period}
                            currentSortColumn={table7SortColumn}
                            currentSortDirection={table7SortDirection}
                            onSort={handleTable7Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table7SortColumn}
                          currentSortDirection={table7SortDirection}
                          onSort={handleTable7Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>
                          OBS Count
                        </TableCell>
                        {data.obsIncidents.timeSeries.map((obs) => (
                          <TableCell key={obs.period} align="center">
                            {obs.count}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, zIndex: 1 }}>
                          {data.obsIncidents.total}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Visual 8: Location Split */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 500, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents by CI Location
                    </Typography>
                    <Chip label={`${sortedLocations.length} total`} size="small" />
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportLocationSplit}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view3aMode}
                      exclusive
                      onChange={(e, val) => val && setView3aMode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ position: 'relative' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 200, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          Location
                        </TableCell>
                        {data.locationSplit.map((split) => (
                          <SortableHeader
                            key={split.period}
                            column={split.period}
                            label={split.period}
                            currentSortColumn={table8SortColumn}
                            currentSortDirection={table8SortDirection}
                            onSort={handleLocationSort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table8SortColumn}
                          currentSortDirection={table8SortDirection}
                          onSort={handleLocationSort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedLocations.slice((locationPage - 1) * rowsPerPage, locationPage * rowsPerPage).map((location) => {
                        const rowTotal = locationTotals.rowTotals[location] || 0;
                        const rowPercentage = locationTotals.grandTotal > 0 ? (rowTotal / locationTotals.grandTotal) * 100 : 0;
                        return (
                          <TableRow key={location}>
                            <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, fontSize: '0.75rem' }}>
                              {location}
                            </TableCell>
                            {data.locationSplit.map((split) => {
                              const count = split.locations[location] || 0;
                              const periodTotal = locationTotals.periodTotals[split.period] || 0;
                              const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
                              return (
                                <TableCell key={split.period} align="center" sx={{ fontSize: '0.75rem' }}>
                                  {view3aMode === 'count' ? count : `${percentage.toFixed(1)}%`}
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 1 }}>
                              {view3aMode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total Row - only on last page, sticky at bottom */}
                      {locationPage === Math.ceil(sortedLocations.length / rowsPerPage) && (
                        <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f7fa', borderTop: '2px solid #ddd', zIndex: 2 }}>
                          <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: '#f5f7fa', zIndex: 3, fontSize: '0.75rem' }}>
                            Total
                          </TableCell>
                          {data.locationSplit.map((split) => {
                            const periodTotal = locationTotals.periodTotals[split.period] || 0;
                            return (
                              <TableCell key={split.period} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                                {view3aMode === 'count' ? periodTotal : '100.0%'}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#e3f2fd', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 3 }}>
                            {view3aMode === 'count' ? locationTotals.grandTotal : '100.0%'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination 
                    count={Math.ceil(sortedLocations.length / rowsPerPage)} 
                    page={locationPage} 
                    onChange={(e, page) => setLocationPage(page)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Visual 9: CI Name Split */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 500, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents by CI Name
                    </Typography>
                    <Chip label={`${sortedCINames.length} total`} size="small" />
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportCINameSplit}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view3bMode}
                      exclusive
                      onChange={(e, val) => val && setView3bMode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ position: 'relative' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 200, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          CI Name
                        </TableCell>
                        {data.ciNameSplit.map((split) => (
                          <SortableHeader
                            key={split.period}
                            column={split.period}
                            label={split.period}
                            currentSortColumn={table9SortColumn}
                            currentSortDirection={table9SortDirection}
                            onSort={handleCINameSort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table9SortColumn}
                          currentSortDirection={table9SortDirection}
                          onSort={handleCINameSort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedCINames.slice((ciNamePage - 1) * rowsPerPage, ciNamePage * rowsPerPage).map((ciName) => {
                        const rowTotal = ciNameTotals.rowTotals[ciName] || 0;
                        const rowPercentage = ciNameTotals.grandTotal > 0 ? (rowTotal / ciNameTotals.grandTotal) * 100 : 0;
                        return (
                          <TableRow key={ciName}>
                            <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, fontSize: '0.75rem' }}>
                              {ciName}
                            </TableCell>
                            {data.ciNameSplit.map((split) => {
                              const count = split.ciNames[ciName] || 0;
                              const periodTotal = ciNameTotals.periodTotals[split.period] || 0;
                              const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
                              return (
                                <TableCell key={split.period} align="center" sx={{ fontSize: '0.75rem' }}>
                                  {view3bMode === 'count' ? count : `${percentage.toFixed(1)}%`}
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 1 }}>
                              {view3bMode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total Row - only on last page, sticky at bottom */}
                      {ciNamePage === Math.ceil(sortedCINames.length / rowsPerPage) && (
                        <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f7fa', borderTop: '2px solid #ddd', zIndex: 2 }}>
                          <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: '#f5f7fa', zIndex: 3, fontSize: '0.75rem' }}>
                            Total
                          </TableCell>
                          {data.ciNameSplit.map((split) => {
                            const periodTotal = ciNameTotals.periodTotals[split.period] || 0;
                            return (
                              <TableCell key={split.period} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                                {view3bMode === 'count' ? periodTotal : '100.0%'}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#e3f2fd', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 3 }}>
                            {view3bMode === 'count' ? ciNameTotals.grandTotal : '100.0%'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination 
                    count={Math.ceil(sortedCINames.length / rowsPerPage)} 
                    page={ciNamePage} 
                    onChange={(e, page) => setCINamePage(page)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Visual 10: CI Class Split */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Incidents by CI Class
                    </Typography>
                    <Chip label={`${sortedCIClasses.length} total`} size="small" />
                    {loading && <CircularProgress size={16} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Export to CSV">
                      <Button
                        size="small"
                        onClick={exportCIClassSplit}
                        startIcon={<FileDownload />}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        CSV
                      </Button>
                    </Tooltip>
                    <ToggleButtonGroup
                      value={view3cMode}
                      exclusive
                      onChange={(e, val) => val && setView3cMode(val)}
                      size="small"
                    >
                      <ToggleButton value="count">Count</ToggleButton>
                      <ToggleButton value="percentage">%</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ position: 'relative' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 200, position: 'sticky', left: 0, top: 0, backgroundColor: 'white', zIndex: 3 }}>
                          CI Class
                        </TableCell>
                        {data.ciClassSplit.map((split) => (
                          <SortableHeader
                            key={split.period}
                            column={split.period}
                            label={split.period}
                            currentSortColumn={table10SortColumn}
                            currentSortDirection={table10SortDirection}
                            onSort={handleTable10Sort}
                            align="center"
                            sx={{ minWidth: 100, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 2 }}
                          />
                        ))}
                        <SortableHeader
                          column="total"
                          label="Total"
                          currentSortColumn={table10SortColumn}
                          currentSortDirection={table10SortDirection}
                          onSort={handleTable10Sort}
                          align="center"
                          sx={{ fontWeight: 700, minWidth: 100, backgroundColor: '#f5f7fa', position: 'sticky', right: 0, top: 0, zIndex: 3 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedCIClasses.map((ciClass) => {
                        const rowTotal = ciClassTotals.rowTotals[ciClass] || 0;
                        const rowPercentage = ciClassTotals.grandTotal > 0 ? (rowTotal / ciClassTotals.grandTotal) * 100 : 0;
                        return (
                          <TableRow key={ciClass}>
                            <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, fontSize: '0.75rem' }}>
                              {ciClass}
                            </TableCell>
                            {data.ciClassSplit.map((split) => {
                              const count = split.ciClasses[ciClass] || 0;
                              const periodTotal = ciClassTotals.periodTotals[split.period] || 0;
                              const percentage = periodTotal > 0 ? (count / periodTotal) * 100 : 0;
                              return (
                                <TableCell key={split.period} align="center" sx={{ fontSize: '0.75rem' }}>
                                  {view3cMode === 'count' ? count : `${percentage.toFixed(1)}%`}
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 1 }}>
                              {view3cMode === 'count' ? rowTotal : `${rowPercentage.toFixed(1)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total Row - sticky at bottom */}
                      <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f7fa', borderTop: '2px solid #ddd', zIndex: 2 }}>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: '#f5f7fa', zIndex: 3, fontSize: '0.75rem' }}>
                          Total
                        </TableCell>
                        {data.ciClassSplit.map((split) => {
                          const periodTotal = ciClassTotals.periodTotals[split.period] || 0;
                          return (
                            <TableCell key={split.period} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                              {view3cMode === 'count' ? periodTotal : '100.0%'}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#e3f2fd', fontSize: '0.75rem', position: 'sticky', right: 0, zIndex: 3 }}>
                          {view3cMode === 'count' ? ciClassTotals.grandTotal : '100.0%'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

          </Grid>
        </>
      )}
      </>
      )}
      </Box>

      {/* Visual View - always mounted */}
      <Box
        sx={{
          ...(activeTab !== 'visual' && {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            visibility: 'hidden',
            pointerEvents: 'none',
          }),
        }}
      >
      {data && (
        <NOCDashboardVisual
          resolutionBuckets={data.resolutionBuckets}
          resolutionCodeSplit={data.resolutionCodeSplit}
          locationSplit={data.locationSplit}
          ciNameSplit={data.ciNameSplit}
          ciClassSplit={data.ciClassSplit}
          mttrBuckets={data.mttrBuckets}
          stateAndHoldReasons={data.stateAndHoldReasons}
          nocTransfers={data.nocTransfers}
          obsIncidents={data.obsIncidents}
          loading={loading}
          selectedLocations={selectedLocations}
        />
      )}
      </Box>

      {/* Raw Data View - always mounted */}
      <Box
        sx={{
          ...(activeTab !== 'rawdata' && {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            visibility: 'hidden',
            pointerEvents: 'none',
          }),
        }}
      >
        {rawDataLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        )}

        {rawDataError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {rawDataError}
          </Alert>
        )}

        {!rawDataLoading && !rawDataError && rawData.length > 0 && rawDataPagination && (
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Raw NOC Incidents Data ({rawDataPagination.total_records.toLocaleString()} records)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Sorting Controls Group */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  alignItems: 'center', 
                  padding: '8px 12px', 
                  border: '1px solid rgba(0, 0, 0, 0.23)', 
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={rawDataSortBy}
                      label="Sort By"
                      onChange={(e) => setRawDataSortBy(e.target.value)}
                    >
                      <MenuItem value="number">Incident ID</MenuItem>
                      <MenuItem value="opened_at">Opened At</MenuItem>
                      <MenuItem value="resolved_at">Resolved At</MenuItem>
                      <MenuItem value="assignment_group">Assignment Group</MenuItem>
                      <MenuItem value="close_code">Close Code</MenuItem>
                      <MenuItem value="state">State</MenuItem>
                      <MenuItem value="hold_reason">Hold Reason</MenuItem>
                      <MenuItem value="sla_assignment_groups">SLA Assignment Groups</MenuItem>
                      <MenuItem value="ci_name">CMDB CI</MenuItem>
                      <MenuItem value="priority">Priority</MenuItem>
                      <MenuItem value="vendor_name">Vendor Name</MenuItem>
                      <MenuItem value="category">Category</MenuItem>
                      <MenuItem value="subcategory">Subcategory</MenuItem>
                      <MenuItem value="opened_by">Opened By</MenuItem>
                      <MenuItem value="ci_class">CI Class</MenuItem>
                      <MenuItem value="ci_location">CI Location</MenuItem>
                      <MenuItem value="mttr">TTR Minutes</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Direction</InputLabel>
                    <Select
                      value={rawDataSortDirection}
                      label="Direction"
                      onChange={(e) => setRawDataSortDirection(e.target.value as 'asc' | 'desc')}
                    >
                      <MenuItem value="asc">Ascending</MenuItem>
                      <MenuItem value="desc">Descending</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={applyRawDataSorting}
                    sx={{
                      backgroundColor: '#0067b3',
                      '&:hover': { backgroundColor: '#005a9e' },
                      minWidth: '80px'
                    }}
                  >
                    Apply
                  </Button>
                </Box>
                {/* Export Button */}
                <Button
                  variant="contained"
                  startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
                  onClick={exportRawData}
                  disabled={isExporting}
                  sx={{
                    backgroundColor: '#0067b3',
                    '&:hover': { backgroundColor: '#005a9e' },
                  }}
                >
                  {isExporting ? 'Exporting...' : 'Export All'}
                </Button>
              </Box>
            </Box>

            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Incident ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Opened At</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Resolved At</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Assignment Group</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Close Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Hold Reason</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>SLA Assignment Groups</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>CMDB CI</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vendor Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Subcategory</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Opened By</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>CI Class</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>CI Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>TTR Minutes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rawData.map((record, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <a
                          href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${record.number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0067b3', textDecoration: 'none' }}
                        >
                          {record.number}
                        </a>
                      </TableCell>
                      <TableCell>{record.opened_at ? new Date(record.opened_at).toLocaleString() : ''}</TableCell>
                      <TableCell>{record.resolved_at ? new Date(record.resolved_at).toLocaleString() : ''}</TableCell>
                      <TableCell>{record.assignment_group_dv}</TableCell>
                      <TableCell>{record.close_code || ''}</TableCell>
                      <TableCell>{record.state_dv}</TableCell>
                      <TableCell>{record.hold_reason}</TableCell>
                      <TableCell>{record.u_sla_assignment_groups_dv}</TableCell>
                      <TableCell>{record.cmdb_ci_dv}</TableCell>
                      <TableCell>{record.priority}</TableCell>
                      <TableCell>{record.u_vendor_name_dv}</TableCell>
                      <TableCell>{record.category_dv}</TableCell>
                      <TableCell>{record.subcategory_dv}</TableCell>
                      <TableCell>{record.opened_by}</TableCell>
                      <TableCell>{record.ci_class}</TableCell>
                      <TableCell>{record.ci_location}</TableCell>
                      <TableCell>{record.mttr_minutes !== null ? record.mttr_minutes.toFixed(2) : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Pagination
                count={rawDataPagination.total_pages}
                page={rawDataPage}
                onChange={(e, page) => setRawDataPage(page)}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          </Paper>
        )}

        {!rawDataLoading && !rawDataError && rawData.length === 0 && rawDataPagination !== null && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No incident data available for the selected filters.
          </Alert>
        )}
      </Box>

      </Box>{/* end container */}
    </Box>
  );
};

export default NOCDashboard;
